import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { styles } from '../../../styles/themes/style';
import {
  ILeadPreferences, EMPTY_LEAD_PREFERENCES, ISequenceStep,
  CampaignExecutionMethod, CampaignStatus, IUnipileConnectForm, linkedInLoginFields,
  ISendingWindow, DEFAULT_SENDING_WINDOW, DAY_OPTIONS,
  ICampaign, emptyLeadStatusCounts, sumEnrolledLeads, ISenderIdentity, emailStepIsIncomplete,
} from '../../../interfaces/models/campaign';
import { useCampaignsRequest, IProxyAccountDetails, IWorkspaceAccount } from '../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../stores/application/useAppActions';
import { AppRoutes } from '../../../interfaces/store/appStore';
import Column from '../../../components/Column';
import Text from '../../../components/Text';
import LoadingSpinner from '../../../components/LoadingSpinner';
import SvgFragment from '../../../fragments/SvgFragment';
import CampaignVariableWarnModal from '../components/campaigns/CampaignVariableWarnModal';
import CampaignSequenceBuilder from '../components/campaigns/CampaignSequenceBuilder';
import { applyDescriptionToSequence } from '../utils/campaignAiPrompts';
import CampaignPanelBoundary from '../components/campaigns/CampaignPanelBoundary';
import CampaignWizardStepper, {
  CAMPAIGN_WIZARD_STEPS, CAMPAIGN_WIZARD_STEP_SETTINGS, CAMPAIGN_WIZARD_STEP_RECIPIENTS,
  CAMPAIGN_WIZARD_STEP_SEQUENCE,
} from '../components/campaigns/CampaignWizardStepper';
import CampaignRecipientsSection, { CampaignWizardLeadSource } from '../components/campaigns/CampaignRecipientsSection';
import CampaignSuppressionLists from '../components/campaigns/CampaignSuppressionLists';
import CampaignAccountPicker, { AccountAvatar } from '../components/campaigns/CampaignAccountPicker';
import { SENDERS_CHANGED_EVENT } from '../utils/senderEvents';
import AddLinkedInAccountModal from '../components/campaigns/AddLinkedInAccountModal';
import CampaignSenderIdentities from '../components/campaigns/CampaignSenderIdentities';
import CampaignEmailCoverage from '../components/campaigns/CampaignEmailCoverage';
import CampaignLeadPreferences from '../components/campaigns/CampaignLeadPreferences';
import CampaignLeadsView from '../components/campaigns/CampaignLeadsView';
import TooltipWrapper from '../../../components/TooltipWrapper';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { useAuthHandlers } from '../../../services/actions/auth/useAuthHandlers';
import {
  CampaignVariableKey,
  ICampaignVariableFallbacks,
  mergeVariableFallbacks,
  normalizeVariableFallbacks,
  unrecognizedSequenceVariableTokens,
} from '../../../utils/campaignTemplateVariables';
import { CAMPAIGN_COMMON_TIMEZONES, formatTimezoneLabel } from '../../../utils/timezoneLabels';
import { formatSequenceSummaryWithDays } from '../../../utils/campaignSequenceSummary';
import { campaignMobile } from '../utils/campaignBreakpoints';
import {
  formatAccountInMailCreditsLine,
  formatAccountProducts,
  formatInMailCreditBalance,
  summarizeInMailCredits,
} from '../utils/inMailCredits';
import {
  checkpointHint, checkpointMode, checkpointToast, LINKEDIN_CHECKPOINT_WINDOW_MS,
} from '../utils/linkedInCheckpoint';

/**
 * Every campaign runs in the cloud for now: the "Where should your campaign run?"
 * picker is hidden and the execution method is pinned to 'proxy', including for
 * campaigns saved earlier as 'extension'.
 *
 * To bring the choice back, flip this to false — the picker markup below is guarded
 * by it and nothing else was removed.
 */
const FORCE_PROXY_EXECUTION: boolean = true;

const DEFAULT_EXECUTION_METHOD: CampaignExecutionMethod = FORCE_PROXY_EXECUTION ? 'proxy' : 'extension';

const TRIAL_PROXY_ACCOUNT_MSG = 'Trial plan allows a single connected LinkedIn account across all campaigns. Upgrade to Growth to connect more.';

/** Shown wherever the campaign is blocked on having no LinkedIn account in its pool. */
const NO_ACCOUNT_MSG = 'Connect a LinkedIn account before proceeding to the next step.';
const WIZARD_LAST_STEP = CAMPAIGN_WIZARD_STEPS.length - 1;
const DEFAULT_LEADS_PER_IMPORT = 50;
const TRIAL_AUTO_IMPORT_LIMIT = 50;

/** Sizes offered by the "Max leads to import" control, trimmed to the account's ceiling. */
const LEAD_LIMIT_PRESETS = [25, 50, 100, 200, 300, 500];

const MAX_LEADS_PER_IMPORT = (() => {
  const raw = process.env.REACT_APP_CAMPAIGN_LEADS_PER_IMPORT_MAX ?? '50';
  const n = parseInt(raw, 10);
  return !isNaN(n) && n >= 1 ? n : 50;
})();

function isSequenceValid(steps: ISequenceStep[]): boolean {
  if (!steps.length) return false;
  // Only the first email needs a subject — a follow-up replying in-thread inherits it.
  const firstEmailIdx = steps.findIndex(s => s.type === 'email');

  return steps.every((s, idx) => {
    if (s.type === 'email') {
      return !emailStepIsIncomplete(s, idx === firstEmailIdx);
    }
    if (s.type === 'sn_message') {
      const body = s.finalContent?.trim()
        || (s.messageMode === 'template' ? s.messageTemplate?.trim() : s.messageAiPrompt?.trim());
      return !!s.messageSubject?.trim() && !!body;
    }
    if (s.type === 'message') {
      if (s.finalContent?.trim()) return true;
      return s.messageMode === 'template' ? !!s.messageTemplate?.trim() : !!s.messageAiPrompt?.trim();
    }
    if (s.type === 'post_comment') {
      if (s.finalContent?.trim()) return true;
      return s.commentMode === 'template' ? !!s.commentTemplate?.trim() : !!s.commentAiPrompt?.trim();
    }
    if (s.type === 'connection_request' && s.includeNote) {
      if (s.finalContent?.trim()) return true;
      return s.messageMode === 'template' ? !!s.messageTemplate?.trim() : !!s.messageAiPrompt?.trim();
    }
    return true;
  });
}

// ── Sending window helpers ───────────────────────────────────────────────────

const COMMON_TIMEZONES = CAMPAIGN_COMMON_TIMEZONES;

const ALL_TIMEZONES: string[] = (() => {
  try { return (Intl as any).supportedValuesOf?.('timeZone') ?? COMMON_TIMEZONES; }
  catch { return COMMON_TIMEZONES; }
})();

const BROWSER_TIMEZONE: string = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch { return ''; }
})();

const fmtHour = (h: number) => `${String(h).padStart(2, '0')}:00`;

// Maps a provider-side account status to a UI badge
function accountBadge(status: string): { label: string; tone: 'green' | 'amber' | 'red' } {
  switch (status) {
    case 'connected':   return { label: 'Connected',            tone: 'green' };
    case 'pending':     return { label: 'Pending verification', tone: 'amber' };
    case 'not_found':   return { label: 'Connection lost',      tone: 'red' };
    case 'error':       return { label: 'Status unavailable',   tone: 'amber' };
    // credentials / stopped / disconnected / any other live-but-broken state
    default:            return { label: 'Reconnect required',   tone: 'amber' };
  }
}

// A disconnected account that can be re-authenticated in place. Excludes healthy
// ('connected'), mid-2FA ('pending'), deleted ('not_found') and transient
// lookup failures ('error').
function canReconnect(status: string): boolean {
  return !['connected', 'pending', 'not_found', 'error'].includes(status);
}

function getAddFormError(form: IUnipileConnectForm): string {
  if (!/^[A-Z]{2}$/.test(form.country.trim().toUpperCase())) return 'Country must be a 2-letter ISO code.';
  if (form.method === 'cookies') {
    if (!form.liAt.trim()) return 'Paste the li_at cookie from this browser.';
    return '';
  }
  const hasEmail = !!form.liEmail.trim();
  const hasPassword = !!form.liPassword;
  if (hasEmail && !hasPassword) return 'Enter LinkedIn password.';
  if (!hasEmail && hasPassword) return 'Enter LinkedIn email.';
  return '';
}

const CampaignFormPage: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getCampaignById, createCampaign, updateCampaign, updateProxyConfig,
    getProxyAccounts, getWorkspaceAccounts,
    // Used when there is no campaign to connect against yet — see submitAddAccount.
    connectWorkspaceLinkedIn, solveWorkspaceLinkedInCheckpoint,
    // The quiet, background versions of the approval check — see the watcher below.
    checkProxyCheckpoint, checkWorkspaceLinkedInCheckpoint,
  } = useCampaignsRequest();
  const { setToast } = useAppActions();
  const { store: { trial, user } } = useAuthStore();
  const { saveUserPreferencesHandler } = useAuthHandlers();

  const maxLeadsPerImport = trial ? TRIAL_AUTO_IMPORT_LIMIT : MAX_LEADS_PER_IMPORT;

  // Create flow stays on /campaigns/new. The id rides in the query so a refresh
  // reopens the same draft without switching to the edit route (that route skips
  // the "add leads / fill sequence" checks).
  const draftIdFromUrl = !isEdit ? (new URLSearchParams(location.search).get('draft') || null) : null;

  const [loading, setLoading]   = useState(isEdit || !!draftIdFromUrl);
  const [saving, setSaving]     = useState(false);
  const [name, setName]         = useState('');
  const [description, setDescription] = useState('');
  const [leadLimit, setLeadLimit]     = useState(String(DEFAULT_LEADS_PER_IMPORT));
  const [leadSource, setLeadSource]   = useState<CampaignWizardLeadSource>('leadsearch');
  const [wizardEnrolledTotal, setWizardEnrolledTotal] = useState(0);
  const [prefs, setPrefs]             = useState<ILeadPreferences>(EMPTY_LEAD_PREFERENCES);
  // New campaigns start on the "Empty" sequence template — the user picks the steps
  // they actually want instead of deleting a pre-filled Connect + Message flow.
  const [sequence, setSequence]       = useState<ISequenceStep[]>([]);
  /** Campaign footer settings. The postal address is required on cold email. */
  const [emailConfig, setEmailConfig] = useState<{ physicalAddress?: string; footer?: string }>({});
  const [variableFallbacks, setVariableFallbacks] = useState<ICampaignVariableFallbacks>(() =>
    mergeVariableFallbacks(undefined),
  );
  const [execMethod, setExecMethod]   = useState<CampaignExecutionMethod>(DEFAULT_EXECUTION_METHOD);

  // Multi-account pool — loaded from campaign (edit) or built during creation
  const [connectedAccountIds, setConnectedAccountIds] = useState<string[]>([]);
  // Accounts awaiting 2FA verification — not used by the executor until verified
  const [pendingAccountIds, setPendingAccountIds] = useState<string[]>([]);
  // Live provider-side details per account (display name + real status)
  const [accountDetails, setAccountDetails] = useState<Record<string, IProxyAccountDetails>>({});
  // Existing workspace accounts available to reuse (account picker dropdown)
  const [workspaceAccounts, setWorkspaceAccounts] = useState<IWorkspaceAccount[]>([]);
  // Accounts picked before the campaign exists — there is nothing to attach them to
  // yet, so they are held here and flushed into the pool once the campaign is created.
  const [deferredAccountIds, setDeferredAccountIds] = useState<string[]>([]);
  // Auto-selection runs once per mount; re-running would re-add an account the user
  // deliberately unchecked.
  const autoSelectedRef = useRef(false);

  // Sender identities (LinkedIn + mailbox, paired) this campaign draws from
  // Loaded sender details — needed to tell "no mailbox" from "mailbox broken".
  const [senderIdentities, setSenderIdentities] = useState<ISenderIdentity[]>([]);

  // Sending window (proxy only — enforced server-side by the cloud executor).
  // New campaigns start in the browser's timezone so the user doesn't have to
  // click "use mine". Edit still overwrites this with the saved window.
  const [sendingWindow, setSendingWindow] = useState<ISendingWindow>({
    ...DEFAULT_SENDING_WINDOW,
    timezone: BROWSER_TIMEZONE || DEFAULT_SENDING_WINDOW.timezone,
  });

  // Opt-in suppression lists this campaign applies (the always-on ones need no choice).
  const [suppressionListIds, setSuppressionListIds] = useState<string[]>([]);


  // Tracks the campaign that already exists (either from route param or created during connect flow)
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(isEdit ? id! : null);

  // Credentials are entered in a modal (add or reconnect) rather than inline.
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);

  // Collapsible settings sections — open by default so the cloud setup is visible.
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [accountsOpen, setAccountsOpen] = useState(true);

  // Checkpoint for 2FA during account connection
  const [checkpoint, setCheckpoint] = useState<{ accountId: string; type: string; campaignId: string } | null>(null);
  const [checkpointCode, setCheckpointCode] = useState('');
  // When set, the credentials form re-authenticates this disconnected account
  // instead of adding a new one.
  const [reconnectingAccountId, setReconnectingAccountId] = useState<string | null>(null);
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>('draft');
  const [wizardStep, setWizardStep] = useState(() => {
    const raw = new URLSearchParams(location.search).get('wizardStep');
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return !Number.isNaN(n) && n >= 0 && n <= WIZARD_LAST_STEP ? n : 0;
  });
  const prevWizardStepRef = useRef(0);
  /*
    Set when a step was opened from an Edit link on the review, so its primary button
    becomes Save-and-return instead of Next. Without it, correcting one field meant
    walking every later step again just to get back to the review.
  */
  const [returnToReview, setReturnToReview] = useState(false);

  // Field-level validation for the settings step. A toast alone left people hunting
  // for what was wrong — these mark the offending field red, and the refs let the
  // step scroll to it.
  const [nameError, setNameError] = useState(false);
  const [accountError, setAccountError] = useState(false);
  const nameFieldRef = useRef<HTMLLabelElement>(null);
  const accountSectionRef = useRef<HTMLDivElement>(null);
  /** One in-flight POST so a debounced save and "Next" can't create two drafts. */
  const draftCreateRef = useRef<Promise<string | null> | null>(null);
  /** Patches run one after another so an older snapshot can't land after a newer one. */
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  /** Skip the snapshot written while the form is hydrating, so opening a draft doesn't PATCH it. */
  const skipAutosaveRef = useRef(true);
  /** Close / launch navigate elsewhere — don't also replace the URL with the edit route. */
  const suppressDraftRedirectRef = useRef(false);
  /** Set while a debounced save is waiting, so leaving the page still flushes the latest fields. */
  const autosaveDirtyRef = useRef(false);
  const saveLatestRef = useRef<() => Promise<string | null>>(async () => null);

  useEffect(() => {
    const loadId = isEdit ? id! : draftIdFromUrl;
    // Adding ?draft= after the first save must not refetch and clobber what the user just typed.
    if (!loadId || (!isEdit && activeCampaignId === loadId)) return;
    getCampaignById(loadId).then(data => {
      if (data?.campaign) {
        const c = data.campaign;
        // Only the creator may edit a campaign — redirect others back to the list.
        if (user?._id && String(c.createdBy) !== String(user._id)) {
          setToast({
            type: 'error',
            message: `Only ${c.createdByName || 'the campaign creator'} can edit this campaign.`,
          });
          navigate(AppRoutes.CAMPAIGNS);
          return;
        }
        setName(c.name ?? '');
        setDescription(c.description ?? '');
        setCampaignStatus(c.status ?? 'draft');
        // The picker only offers sizes up to this account's ceiling, so a campaign saved
        // above it (created on a higher plan) is clamped here — and said out loud, rather
        // than leaving the user to notice their campaign stops filling early.
        const storedLimit = c.leadLimit ?? DEFAULT_LEADS_PER_IMPORT;
        if (storedLimit > maxLeadsPerImport) {
          setToast({
            type: 'info',
            message: trial
              ? `Trial accounts can import up to ${maxLeadsPerImport} leads per campaign — set to ${maxLeadsPerImport}.`
              : `A campaign can hold up to ${maxLeadsPerImport} LeadSearch leads — set to ${maxLeadsPerImport}.`,
          });
        }
        setLeadLimit(String(Math.min(storedLimit, maxLeadsPerImport)));
        // Merge over EMPTY so campaigns saved before a filter existed still get every array.
        const loadedPrefs = { ...EMPTY_LEAD_PREFERENCES, ...(c.leadPreferences ?? {}) };
        setPrefs(loadedPrefs);
        // Prefer the stored source. The inference below is only a fallback for
        // campaigns created before leadSource was persisted — it cannot tell a CSV /
        // contacts / posts campaign apart from a custom one, which is why those
        // campaigns used to open with the recipients step skipped.
        const loadedHasPrefs = Object.values(loadedPrefs).some(
          v => Array.isArray(v) ? v.length > 0 : !!v,
        );
        setLeadSource(c.leadSource ?? (!loadedHasPrefs && !c.lastFetchedAt ? 'empty' : 'leadsearch'));
        if (c.sequence?.length) setSequence(c.sequence);
        setVariableFallbacks(mergeVariableFallbacks(
          user?.preferences?.campaignVariableFallbacks,
          c.variableFallbacks,
        ));
        setEmailConfig({
          physicalAddress: c.emailConfig?.physicalAddress ?? '',
          footer: c.emailConfig?.footer ?? '',
        });
        setExecMethod(FORCE_PROXY_EXECUTION ? 'proxy' : (c.executionMethod ?? 'extension'));
        setConnectedAccountIds(c.unipileConfig?.accountIds ?? []);
        setPendingAccountIds(c.unipileConfig?.pendingAccountIds ?? []);
        if (c.sendingWindow) setSendingWindow({ ...DEFAULT_SENDING_WINDOW, ...c.sendingWindow });
        setSuppressionListIds((c.suppressionListIds ?? []).map(String));
        if (!isEdit) {
          setActiveCampaignId(loadId);
          // Review doesn't mount the leads table, so the Start button would stay
          // disabled after a refresh unless this count is restored with the draft.
          setWizardEnrolledTotal(sumEnrolledLeads(c.statusCounts));
        }
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, draftIdFromUrl]);

  useEffect(() => {
    if (isEdit || draftIdFromUrl) return;
    setVariableFallbacks(mergeVariableFallbacks(
      user?.preferences?.campaignVariableFallbacks,
    ));
  }, [isEdit, draftIdFromUrl, user?.preferences?.campaignVariableFallbacks]);

  // Deep-link from Leads → "Edit targeting filters" opens the targeting wizard step.
  //
  // Also restores the step after the mailbox OAuth round-trip. That comes back as a
  // full page load from the provider, so router state is gone by then — the step
  // rides along in the query string instead.
  useEffect(() => {
    const fromState = (location.state as { wizardStep?: number } | null)?.wizardStep;
    const fromQuery = new URLSearchParams(location.search).get('wizardStep');
    const step = fromState ?? (fromQuery != null ? parseInt(fromQuery, 10) : null);

    if (!isEdit || step == null || Number.isNaN(step) || step < 0 || step > WIZARD_LAST_STEP) return;
    setWizardStep(step);
    // Keep the step in the query so a refresh reopens the same one. Router state
    // from Leads → "Edit targeting" is copied into that query, then dropped.
    const params = new URLSearchParams(location.search);
    params.set('wizardStep', String(step));
    const search = `?${params.toString()}`;
    if (fromState != null || location.search !== search) {
      navigate({ pathname: location.pathname, search }, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  // The step lives in the URL, not on the campaign. Refresh reads it back above.
  // Draft id is written here too, so a step change can't replace the query and drop it.
  useEffect(() => {
    if (loading || (!isEdit && !activeCampaignId)) return;
    const params = new URLSearchParams(location.search);
    let changed = false;
    if (!isEdit && activeCampaignId && params.get('draft') !== activeCampaignId) {
      params.set('draft', activeCampaignId);
      changed = true;
    }
    if (wizardStep === 0) {
      if (params.has('wizardStep')) {
        params.delete('wizardStep');
        changed = true;
      }
    } else if (params.get('wizardStep') !== String(wizardStep)) {
      params.set('wizardStep', String(wizardStep));
      changed = true;
    }
    if (!changed) return;
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, isEdit, loading, activeCampaignId]);

  // Refresh live account details (name + provider status + InMail credits) whenever the pool changes
  useEffect(() => {
    const campaignId = activeCampaignId ?? (isEdit ? id! : null);
    if (!campaignId || execMethod !== 'proxy') return;
    if (connectedAccountIds.length === 0 && pendingAccountIds.length === 0) return;
    getProxyAccounts(campaignId).then(data => {
      const list: IProxyAccountDetails[] = data?.accounts ?? [];
      if (list.length) setAccountDetails(Object.fromEntries(list.map(a => [a.accountId, a])));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaignId, isEdit, id, execMethod, connectedAccountIds.join(','), pendingAccountIds.join(',')]);

  // Re-fetch credits when opening the review step (pool may have connected after first load).
  useEffect(() => {
    if (wizardStep !== WIZARD_LAST_STEP || execMethod !== 'proxy') return;
    const campaignId = activeCampaignId ?? (isEdit ? id! : null);
    if (!campaignId || (!connectedAccountIds.length && !pendingAccountIds.length)) return;
    getProxyAccounts(campaignId).then(data => {
      const list: IProxyAccountDetails[] = data?.accounts ?? [];
      if (list.length) setAccountDetails(Object.fromEntries(list.map(a => [a.accountId, a])));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep]);

  // Load workspace accounts available to reuse (for the dropdown). Workspace-scoped,
  // so it shows during creation too. Refreshes when the pool changes.
  useEffect(() => {
    if (execMethod !== 'proxy') { setWorkspaceAccounts([]); return; }
    loadWorkspaceAccounts();
    // An account connected in Sending accounts changes no pool, so nothing above
    // would refetch — and the picker would not offer it until a reload.
    const onSendersChanged = () => { loadWorkspaceAccounts(); };
    window.addEventListener(SENDERS_CHANGED_EVENT, onSendersChanged);
    return () => window.removeEventListener(SENDERS_CHANGED_EVENT, onSendersChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaignId, isEdit, id, execMethod, connectedAccountIds.join(','), pendingAccountIds.join(',')]);

  // A workspace that already has a healthy LinkedIn account shouldn't make the user
  // pick it by hand — preselect one so a new campaign can go straight to Next step.
  // The user's own account wins when the workspace has several. Create mode only:
  // an existing campaign's pool is whatever was saved on it.
  useEffect(() => {
    if (isEdit || autoSelectedRef.current || execMethod !== 'proxy') return;
    if (connectedAccountIds.length || pendingAccountIds.length) return;
    const healthy = workspaceAccounts.filter(a => a.status === 'connected');
    if (!healthy.length) return;
    const ownEmail = user?.email?.trim().toLowerCase();
    const preferred = healthy.find(a => !!ownEmail && a.email?.trim().toLowerCase() === ownEmail)
      ?? healthy[0];
    autoSelectedRef.current = true;
    void handleAddExisting(preferred.accountId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, execMethod, workspaceAccounts, connectedAccountIds.length, pendingAccountIds.length]);

  // Clear the inline errors as soon as the user fixes what they flagged.
  useEffect(() => { if (name.trim()) setNameError(false); }, [name]);
  useEffect(() => {
    if (connectedAccountIds.length || pendingAccountIds.length) setAccountError(false);
  }, [connectedAccountIds.length, pendingAccountIds.length]);

  // Pre-fill empty AI prompts when the user opens the sequence step (wizard create only).
  useEffect(() => {
    const enteredSequence = !isEdit && wizardStep === CAMPAIGN_WIZARD_STEP_SEQUENCE && prevWizardStepRef.current !== CAMPAIGN_WIZARD_STEP_SEQUENCE;
    prevWizardStepRef.current = wizardStep;
    if (!enteredSequence || !description.trim()) return;
    setSequence(prev => applyDescriptionToSequence(description, prev));
  }, [wizardStep, isEdit, description]);

  // ── Account management (edit mode — immediate) ─────────────────────────────

  // A LinkedIn login through the provider takes 1-2 minutes; reverse proxies in
  // front of the backend can cut the HTTP response before it lands even though
  // the backend finishes the login and stores the outcome. When the connect
  // request dies, poll the campaign until the stored result appears instead of
  // reporting a false failure.
  async function pollConnectOutcome(
    campaignId: string,
    knownIds: string[],
  ): Promise<'connected' | 'checkpoint' | null> {
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise(r => setTimeout(r, 12000));
      const data = await getCampaignById(campaignId);
      const cfg = data?.campaign?.unipileConfig;
      if (!cfg) continue;
      const pending: string[] = cfg.pendingAccountIds ?? [];
      const active: string[]  = cfg.accountIds ?? [];
      if (pending.length) {
        setConnectedAccountIds(active);
        setPendingAccountIds(pending);
        setCheckpoint({ accountId: pending[pending.length - 1], type: '', campaignId });
        setCheckpointCode('');
        return 'checkpoint';
      }
      const newId = active.find(a => !knownIds.includes(a));
      if (newId) {
        setConnectedAccountIds(active);
        return 'connected';
      }
    }
    return null;
  }

  async function handleAddAccount(form: IUnipileConnectForm) {
    /*
      Trial users: allow only 1 cloud (proxy) LinkedIn account.

      Counted from CONNECTED accounts only. A pending one is an attempt still being
      verified — or one that failed and was never cleared — and counting it meant the
      trial's single seat was taken by the very attempt the user was trying to redo:
      "Trial plan allows only 1 LinkedIn account" with nothing connected at all. The
      server still enforces the cap for real, and clears a dead attempt before refusing.
    */
    const poolCount = connectedAccountIds.length;
    if (trial && poolCount >= 1) {
      setToast({ type: 'error', message: TRIAL_PROXY_ACCOUNT_MSG });
      return;
    }
    const err = getAddFormError(form);
    if (err) { setToast({ type: 'error', message: err }); return; }
    if (form.method !== 'cookies' && (!form.liEmail.trim() || !form.liPassword)) {
      setToast({ type: 'error', message: 'Enter LinkedIn email and password.' }); return;
    }

    setAddingAccount(true);

    /*
      No campaign yet and no name to make one with — connect to the WORKSPACE instead.

      A LinkedIn account belongs to the workspace, not to the campaign that happened to
      be open when it was connected, so nothing about the login actually needs a
      campaign. Refusing here meant a two-minute login, a password and possibly a 2FA
      approval were all blocked behind naming a campaign the user had not got to yet.
      The account is remembered as deferred and attached by flushDeferredAccounts the
      moment the campaign is created.

      The name is still required to SAVE a campaign. It just stops being required to
      connect an account.
    */
    let resolvedId: string = activeCampaignId ?? '';
    if (!resolvedId && !name.trim()) {
      const wsResult: any = await connectWorkspaceLinkedIn({
        ...linkedInLoginFields(form),
        ...(form.country ? { country: form.country } : {}),
      });
      setAddingAccount(false);

      const data = wsResult?.data ?? wsResult;
      if (data?.status === 'connected' && data.accountId) {
        setDeferredAccountIds(prev => Array.from(new Set([...prev, String(data.accountId)])));
        setConnectedAccountIds(prev => Array.from(new Set([...prev, String(data.accountId)])));
        setShowAccountModal(false);
        loadWorkspaceAccounts();
        setToast({ type: 'success', message: 'LinkedIn account connected.' });
        return;
      }
      if (data?.status === 'checkpoint' && data.accountId) {
        // campaignId '' marks this as the workspace flow — see handleCheckpointSubmit.
        setCheckpoint({ accountId: String(data.accountId), type: data.checkpointType ?? '', campaignId: '' });
        /*
          Pending only — NOT deferred. flushDeferredAccounts attaches accounts to the
          campaign's ACTIVE pool, so deferring one that is still mid-verification put an
          unverified account where the executor sends from, and made the campaign derive
          a sender from an account the provider could not yet describe: the nameless
          "Unnamed sender" row. It is attached once the verification succeeds — see
          onCheckpointConnected.
        */
        setPendingAccountIds(prev => Array.from(new Set([...prev, String(data.accountId)])));
        setShowAccountModal(false);
        setToast({ type: 'error', message: checkpointToast(data.checkpointType) });
        return;
      }
      setToast({ type: 'error', message: data?.reason || wsResult?.message || 'Could not connect account.' });
      return;
    }

    // Same create path as autosave — a second POST here used to leave two drafts
    // when the name debounce and "connect account" overlapped.
    if (!resolvedId) {
      const savedId = await saveWizardDraft();
      if (!savedId) {
        setAddingAccount(false);
        setToast({ type: 'error', message: 'Could not create campaign.' });
        return;
      }
      resolvedId = savedId;
    } else if (execMethod === 'proxy') {
      // Existing campaign switched from Extension → Cloud in the editor: the toggle
      // only updates local state, so the campaign may still be stored as 'extension'.
      // Persist the execution method first, otherwise proxy-config rejects the connect
      // with "Campaign does not use proxy execution."
      await updateCampaign(resolvedId, { executionMethod: 'proxy' });
    }

    // Anything the user checked while the campaign was still nameless.
    await flushDeferredAccounts(resolvedId);

    const result = await updateProxyConfig(resolvedId, {
      ...linkedInLoginFields(form),
      country: form.country,
    });

    if (result === null) {
      setAddingAccount(false);
      return;
    }

    // No usable response — the request died mid-flight while the backend may
    // still be finishing the login. Poll for the stored outcome (up to ~2 min).
    if (!result?.status) {
      setToast({ type: 'error', message: 'Connection is taking longer than usual — waiting for LinkedIn…' });
      const outcome = await pollConnectOutcome(resolvedId, connectedAccountIds);
      setAddingAccount(false);
      if (outcome === 'checkpoint') {
        setShowAccountModal(false);
        // The type is not known on this path (pollConnectOutcome recovers only the id),
        // so this stays generic — the box below offers both a code and "already approved".
        setToast({ type: 'error', message: checkpointToast('') });
      } else if (outcome === 'connected') {
        setShowAccountModal(false);
        setToast({ type: 'success', message: 'LinkedIn account connected.' });
      } else {
        setToast({ type: 'error', message: 'Could not confirm the connection — refresh the page in a minute and check the accounts list.' });
      }
      return;
    }

    setAddingAccount(false);
    if (result?.status === 'connected') {
      setConnectedAccountIds(prev => Array.from(new Set([...prev, result.accountId])));
      setShowAccountModal(false);
      loadWorkspaceAccounts();
      setToast({
        type: 'success',
        message: result.reused
          ? 'Account already connected on Unipile — added instantly.'
          : 'LinkedIn account connected.',
      });
    } else if (result?.status === 'checkpoint') {
      setCheckpoint({ accountId: result.accountId, type: result.checkpointType ?? '', campaignId: resolvedId });
      setPendingAccountIds(prev => Array.from(new Set([...prev, result.accountId])));
      setShowAccountModal(false);
      setToast({ type: 'error', message: checkpointToast(result.checkpointType) });
    } else {
      // The modal stays open so the user can correct the credentials in place.
      setToast({ type: 'error', message: result?.reason || 'Could not connect account.' });
    }
  }

  // ── Reuse an existing workspace account (added without re-entering credentials) ──
  // Workspace-scoped, so it also populates during creation (before a campaign exists).
  const loadWorkspaceAccounts = useCallback(async () => {
    const excludeId = activeCampaignId ?? (isEdit ? id! : undefined);
    const data = await getWorkspaceAccounts(excludeId);
    setWorkspaceAccounts((data?.accounts ?? []) as IWorkspaceAccount[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaignId, isEdit, id]);

  /**
   * Attach the accounts chosen before the campaign existed. Called right after a
   * campaign is created, so a selection made on an unnamed draft isn't lost.
   * Returns the ids that were actually attached.
   */
  async function flushDeferredAccounts(campaignId: string, extraId?: string): Promise<string[]> {
    const ids = Array.from(new Set([...deferredAccountIds, ...(extraId ? [extraId] : [])]));
    if (!ids.length) return [];
    setDeferredAccountIds([]);
    const result = await updateProxyConfig(campaignId, { addAccountIds: ids });
    if (!result?.added) {
      // Roll the optimistic selection back — the pool on the server has none of them.
      setConnectedAccountIds(prev => prev.filter(a => !ids.includes(a)));
      setToast({ type: 'error', message: 'Could not add the selected LinkedIn account to this campaign.' });
      return [];
    }
    setConnectedAccountIds(prev => Array.from(new Set([...prev, ...ids])));
    return ids;
  }

  async function handleAddExisting(accountId: string, silent = false) {
    if (!accountId) return;

    const existingId = activeCampaignId ?? (isEdit ? id! : '');
    // No campaign to attach to, and no name to create one with. Keep the choice
    // locally so the picker still works — flushDeferredAccounts persists it as soon
    // as the campaign is created (Next step, draft save, or connecting an account).
    if (!existingId && !name.trim()) {
      setDeferredAccountIds(prev => Array.from(new Set([...prev, accountId])));
      setConnectedAccountIds(prev => Array.from(new Set([...prev, accountId])));
      return;
    }

    setAddingAccount(true);

    // Create mode: make sure the campaign exists first (create it silently), like
    // the credentials flow does — then the account can be added to its pool.
    let resolvedId: string = existingId;
    if (!resolvedId) {
      const savedId = await saveWizardDraft();
      if (!savedId) {
        setAddingAccount(false);
        setToast({ type: 'error', message: 'Could not create campaign.' });
        return;
      }
      resolvedId = savedId;
    } else if (execMethod === 'proxy') {
      await updateCampaign(resolvedId, { executionMethod: 'proxy' });
    }

    // Anything picked while the campaign was nameless rides along in the same call.
    const added = await flushDeferredAccounts(resolvedId, accountId);
    setAddingAccount(false);
    if (!added.length) return;
    loadWorkspaceAccounts();   // refresh so the picker sees the new membership
    if (!silent) setToast({ type: 'success', message: 'Account added to this campaign.' });
  }

  // Re-authenticate a disconnected account (Unipile status CREDENTIALS) in place,
  // reusing the credentials form. Keeps the same account_id, so no cap impact.
  async function handleReconnectAccount(form: IUnipileConnectForm) {
    if (!reconnectingAccountId) return;
    const err = getAddFormError(form);
    if (err) { setToast({ type: 'error', message: err }); return; }
    if (form.method !== 'cookies' && (!form.liEmail.trim() || !form.liPassword)) {
      setToast({ type: 'error', message: 'Enter LinkedIn email and password.' }); return;
    }

    const campaignId = activeCampaignId ?? id!;
    const accountId  = reconnectingAccountId;
    setAddingAccount(true);
    const result = await updateProxyConfig(campaignId, {
      reconnectAccountId: accountId,
      ...linkedInLoginFields(form),
      country: form.country,
    });
    setAddingAccount(false);
    if (!result) return;

    if (result.status === 'connected') {
      setConnectedAccountIds(prev => Array.from(new Set([...prev, accountId])));
      setPendingAccountIds(prev => prev.filter(a => a !== accountId));
      setAccountDetails(prev => ({
        ...prev,
        [accountId]: { ...(prev[accountId] ?? { accountId, name: null, pending: false }), status: 'connected' },
      }));
      setReconnectingAccountId(null);
      setShowAccountModal(false);
      setToast({ type: 'success', message: 'LinkedIn account reconnected.' });
    } else if (result?.status === 'checkpoint') {
      setCheckpoint({ accountId: result.accountId, type: result.checkpointType ?? '', campaignId });
      setPendingAccountIds(prev => Array.from(new Set([...prev, result.accountId])));
      setReconnectingAccountId(null);
      setShowAccountModal(false);
      setToast({ type: 'error', message: checkpointToast(result.checkpointType) });
    } else {
      setToast({ type: 'error', message: result?.reason || 'Could not reconnect account.' });
    }
  }

  /** Modal submit — add a new account, or re-authenticate the one being reconnected. */
  function handleAccountModalSubmit(form: IUnipileConnectForm) {
    if (reconnectingAccountId) void handleReconnectAccount(form);
    else void handleAddAccount(form);
  }

  function closeAccountModal() {
    if (addingAccount) return;
    setShowAccountModal(false);
    setReconnectingAccountId(null);
  }

  // Unchecking an account in the picker, or Remove on its row, takes it out of THIS campaign only — the
  // session stays alive at the provider for the other campaigns using it. A full
  // teardown is "Delete sender" in Settings → Sending accounts.
  async function handleRemoveFromCampaign(accountId: string) {
    const campaignId = activeCampaignId ?? (isEdit ? id! : null);
    // Never persisted (picked before the campaign existed) — drop it locally.
    if (!campaignId || deferredAccountIds.includes(accountId)) {
      setDeferredAccountIds(prev => prev.filter(a => a !== accountId));
      setConnectedAccountIds(prev => prev.filter(a => a !== accountId));
      setPendingAccountIds(prev => prev.filter(a => a !== accountId));
      return;
    }
    setAddingAccount(true);
    const result = await updateProxyConfig(campaignId, { removeAccountId: accountId });
    setAddingAccount(false);
    if (!result?.updated) {
      setToast({ type: 'error', message: 'Could not remove account from this campaign.' });
      return;
    }
    setConnectedAccountIds(prev => prev.filter(a => a !== accountId));
    setPendingAccountIds(prev => prev.filter(a => a !== accountId));
    if (checkpoint?.accountId === accountId) { setCheckpoint(null); setCheckpointCode(''); }
    loadWorkspaceAccounts();
    setToast({ type: 'success', message: 'Account removed from this campaign.' });
  }

  function handleAccountPickerToggle(accountId: string, checked: boolean) {
    if (checked) void handleAddExisting(accountId);
    else void handleRemoveFromCampaign(accountId);
  }

  // forceEmptyCode: used when the checkpoint type is unknown (e.g. reopening a
  // pending account from the list, where we never learned its real type) and the
  // user tells us they already approved in the LinkedIn app — try the no-code path
  // even though the UI defaulted to requiring a code for an unrecognized type.
  /*
    Ask whether a checkpoint is solved. `code` is omitted for an in-app approval.

    An empty campaignId means the connection was started against the workspace,
    because there was no campaign to start it against — so it has to be finished the
    same way. Sending it to the campaign endpoint would have nothing to address.

    `quiet` is the background watcher's variant: its own request slugs, so it and a
    click on the button never cancel each other, and no error toasts from a question
    asked every few seconds.
  */
  async function askCheckpoint(
    cp: { accountId: string; campaignId: string },
    code?: string,
    quiet = false,
  ): Promise<any> {
    if (cp.campaignId) {
      if (quiet && !code) return checkProxyCheckpoint(cp.campaignId, cp.accountId);
      return updateProxyConfig(
        cp.campaignId,
        code ? { accountId: cp.accountId, checkpointCode: code } : { accountId: cp.accountId },
      );
    }
    const res: any = quiet && !code
      ? await checkWorkspaceLinkedInCheckpoint(cp.accountId)
      : await solveWorkspaceLinkedInCheckpoint({ accountId: cp.accountId, ...(code ? { code } : {}) });
    return res?.data;
  }

  /*
    Everything that has to happen once LinkedIn confirms an account — whichever way the
    confirmation arrived: a typed code, the "I've approved" button, or the watcher below
    noticing the phone approval by itself.
  */
  function onCheckpointConnected(cp: { accountId: string; campaignId: string }) {
    setPendingAccountIds(prev => prev.filter(a => a !== cp.accountId));
    setCheckpoint(null);
    setCheckpointCode('');
    setToast({ type: 'success', message: 'LinkedIn account connected.' });

    if (cp.campaignId) {
      setConnectedAccountIds(prev => Array.from(new Set([...prev, cp.accountId])));
      loadWorkspaceAccounts();
      return;
    }
    /*
      Started against the workspace because no campaign existed yet. Only now that it
      is verified is it attached — straight to the campaign if one has been created in
      the meantime, otherwise held until it is (flushDeferredAccounts).
    */
    if (activeCampaignId) {
      void handleAddExisting(cp.accountId, true);
    } else {
      setDeferredAccountIds(prev => Array.from(new Set([...prev, cp.accountId])));
      setConnectedAccountIds(prev => Array.from(new Set([...prev, cp.accountId])));
    }
    loadWorkspaceAccounts();
  }

  /*
    Watch for an in-app approval without waiting for a click.

    Unipile's documented contract for IN_APP_VALIDATION is that nothing tells you the
    phone approval happened — you poll the account or listen for the status webhook.
    The page did neither until "I've approved — Continue" was pressed, so someone who
    approved on their phone and then looked back at the screen saw "Pending
    verification" sitting there, and only a refresh (which re-read the account from
    elsewhere) made it right.

    So while an approval is on screen, it is checked every few seconds for as long as
    LinkedIn keeps the attempt alive. The button still works; it just stops being the
    only way.

    Refs rather than closures: the watcher outlives several renders, and must call the
    CURRENT onCheckpointConnected (which reads the campaign id at the moment of
    success), and must not overlap a check the button is already running.
  */
  const manualCheckRef = useRef(false);
  const onConnectedRef = useRef(onCheckpointConnected);
  onConnectedRef.current = onCheckpointConnected;
  const askRef = useRef(askCheckpoint);
  askRef.current = askCheckpoint;

  useEffect(() => {
    if (!checkpoint || checkpointMode(checkpoint.type) !== 'approve') return;
    const cp = { accountId: checkpoint.accountId, campaignId: checkpoint.campaignId, type: checkpoint.type };
    const startedAt = Date.now();
    let stopped = false;
    let timer: number | undefined;

    const tick = async () => {
      if (stopped) return;
      if (Date.now() - startedAt > LINKEDIN_CHECKPOINT_WINDOW_MS) {
        // Unipile drops the attempt after five minutes; say so instead of going quiet.
        setToast({ type: 'error', message: 'This LinkedIn sign-in expired before it was approved. Cancel and start over to try again.' });
        return;
      }
      if (!manualCheckRef.current) {
        const result: any = await askRef.current(cp, undefined, true).catch(() => null);
        if (stopped) return;
        if (result?.status === 'connected') { onConnectedRef.current(cp); return; }
        if (result?.status === 'checkpoint') {
          const next = String(result.checkpointType ?? '');
          if (next && next.toUpperCase() !== cp.type.toUpperCase()) {
            // A different challenge — this watcher's job is done; the box re-renders
            // for the new one, and if that is an approval too, a new watcher starts.
            setCheckpoint(c => (c && c.accountId === cp.accountId ? { ...c, type: next } : c));
            return;
          }
        } else if (result?.status === 'failed') {
          // Terminal — the attempt expired, or LinkedIn wants a browser check. Say
          // why once and stop asking; the box offers Cancel to start over.
          setToast({ type: 'error', message: result.reason || 'LinkedIn did not confirm this sign-in.' });
          return;
        }
      }
      timer = window.setTimeout(tick, 4000);
    };

    timer = window.setTimeout(tick, 4000);
    return () => { stopped = true; if (timer) window.clearTimeout(timer); };
    // Keyed on the challenge itself — the object is replaced on every type change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkpoint?.accountId, checkpoint?.type, checkpoint?.campaignId]);

  /*
    A way out of a verification that is not going to finish.

    The box had none: while it was open the account picker was disabled and every
    Disconnect button hidden, so a checkpoint that failed for good — expired, or
    refused by LinkedIn — left the page with no control that did anything, and the
    half-made account went on holding the seat. Only a reload got out of it.

    Cancelling takes the attempt out of the pool (and off the provider, where nothing
    else uses it), which is what frees the seat for the next try.
  */
  async function cancelCheckpoint() {
    if (!checkpoint) return;
    const cp = checkpoint;
    setCheckpoint(null);
    setCheckpointCode('');
    setPendingAccountIds(prev => prev.filter(a => a !== cp.accountId));
    if (cp.campaignId) {
      setAddingAccount(true);
      await updateProxyConfig(cp.campaignId, { disconnectAccountId: cp.accountId });
      setAddingAccount(false);
    }
    loadWorkspaceAccounts();
    setToast({ type: 'success', message: 'Verification cancelled. You can connect the account again.' });
  }

  async function handleCheckpointSubmit(forceEmptyCode = false) {
    if (!checkpoint) return;
    const cp = checkpoint;
    const mode = checkpointMode(cp.type);
    // In-app validation has no code — the user just approves in the LinkedIn app.
    if (!forceEmptyCode && mode === 'code' && !checkpointCode.trim()) return;
    if (mode === 'blocked') return;
    const usesEmptyCode = forceEmptyCode || mode === 'approve';
    const code = usesEmptyCode ? undefined : checkpointCode.trim();
    setAddingAccount(true);
    manualCheckRef.current = true;

    const ask = (): Promise<any> => askCheckpoint(cp, code);

    let result: any = await ask();

    /*
      An in-app approval is waited for, not re-clicked.

      LinkedIn does not hand the approval to Unipile the instant it is tapped, and
      Unipile cannot resolve the account until it does — so the first answer after
      "I've approved" is reliably "not yet". Asking once and reporting that as a
      failure is what turned this into three clicks and two alarming toasts for a
      sign-in that was already approved.

      Only the no-code path polls. A typed code is either right or wrong the moment it
      is submitted, and re-sending it would just be retrying a wrong code.
    */
    if (usesEmptyCode) {
      const deadline = Date.now() + 60_000;
      while (result?.status === 'checkpoint' && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 3000));
        result = await ask();
      }
    }

    manualCheckRef.current = false;
    setAddingAccount(false);
    if (!result) return;
    if (result.status === 'connected') {
      onCheckpointConnected(cp);
    } else if (result?.status === 'checkpoint') {
      /*
        A checkpoint coming back does not mean the code was wrong.

        LinkedIn chains verifications — an OTP is regularly followed by an in-app
        approval — and Unipile answers the accepted code with the NEXT checkpoint. A
        different type is therefore progress, and calling it "Incorrect code" told
        people their correct code had failed, on the one screen where they had no way
        to tell otherwise. Only the same challenge coming back again means it was wrong.
      */
      const nextType = String(result.checkpointType ?? '');
      const anotherRound = !!nextType && nextType.toUpperCase() !== cp.type.toUpperCase();

      setCheckpoint(c => (c ? { ...c, type: nextType || c.type } : c));
      setCheckpointCode('');
      setToast(
        anotherRound
          ? { type: 'success', message: 'Code accepted — LinkedIn is asking for one more step.' }
          : {
            type: 'error',
            message: usesEmptyCode
              // Said only after a full minute of asking, so it now means what it says.
              ? 'LinkedIn has not confirmed the approval yet. Check the LinkedIn app for a pending request, then try again.'
              : 'Incorrect code, try again.',
          },
      );
    } else {
      setToast({ type: 'error', message: result?.reason || 'Checkpoint verification failed.' });
    }
  }

  // ── Main form submit ───────────────────────────────────────────────────────

  const sequenceLocked = isEdit && campaignStatus !== 'draft';
  // Pause opens the editor. The message can change; the steps cannot.
  const sequenceTextOnly = isEdit && campaignStatus === 'paused';

  // Email only enters the picture when the sequence actually has an email step —
  // everything email-related below is gated on this.
  const sequenceHasEmailStep = sequence.some(s => s.type === 'email');
  const emailStepPreference = sequence.find(s => s.type === 'email')?.emailPreference;

  const handleVariableFallbackChange = useCallback((
    key: CampaignVariableKey,
    val: string,
    saveAsUserDefault = true,
  ) => {
    setVariableFallbacks(prev => ({ ...prev, [key]: val }));
    // Always keep user defaults in sync so the next campaign inherits this value.
    // saveAsUserDefault stays true unless the popover explicitly opts out.
    if (saveAsUserDefault !== false) {
      const nextUserDefaults = {
        ...normalizeVariableFallbacks(user?.preferences?.campaignVariableFallbacks),
        [key]: val,
      };
      void saveUserPreferencesHandler({ campaignVariableFallbacks: nextUserDefaults });
    }
  }, [user?.preferences?.campaignVariableFallbacks, saveUserPreferencesHandler]);

  // Changing step swaps the whole form body. The browser keeps the scroll offset of the
  // step you left, so a short step opens part-way down — jump back to the top of the card.
  const formBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    formBodyRef.current?.scrollTo({ top: 0 });
  }, [wizardStep]);

  // Preset import sizes for the header control. A campaign saved with a value that is not
  // a preset (older campaigns, or one edited before this control existed) keeps it as an
  // option so opening the wizard never silently changes the user's number.
  const leadLimitOptions = React.useMemo(() => {
    const opts = LEAD_LIMIT_PRESETS.filter(n => n <= maxLeadsPerImport);
    const current = parseInt(leadLimit, 10);
    if (!Number.isNaN(current) && current > 0 && current <= maxLeadsPerImport && !opts.includes(current)) {
      opts.push(current);
    }
    return opts.sort((a, b) => a - b);
  }, [maxLeadsPerImport, leadLimit]);

  const parsedLeadLimit = () => {
    const parsed = parseInt(leadLimit, 10);
    return !isNaN(parsed) && parsed >= 1 ? Math.min(parsed, maxLeadsPerImport) : DEFAULT_LEADS_PER_IMPORT;
  };

  // Active and stopped campaigns omit the sequence so Save can still update
  // the name and window. A paused campaign sends it: the text may have changed,
  // and the backend rejects anything that is not a text change.
  function buildCampaignPatch() {
    return {
      name: name.trim(),
      description: description.trim(),
      leadPreferences: prefs,
      leadLimit: parsedLeadLimit(),
      leadSource,
      executionMethod: execMethod,
      /*
        Deliberately not behind sequenceLocked. The postal address is a footer the
        law requires on cold email, not sequence content, and the backend accepts it
        at any status — so a running campaign that went out without one can still be
        corrected without stopping it.
      */
      emailConfig,
      // Fallbacks stay out of a paused save. The form merges the user's defaults
      // into that state, and writing them back would change what an empty name
      // sends, even when the user only edited the message.
      ...((!sequenceLocked || sequenceTextOnly) && { sequence }),
      ...(!sequenceLocked && { variableFallbacks }),
      ...(execMethod === 'proxy' && { sendingWindow }),
      suppressionListIds,
    };
  }


  const wizardCampaign = useMemo((): ICampaign | null => {
    if (!activeCampaignId || !user?._id) return null;
    return {
      _id: activeCampaignId,
      name: name.trim() || 'Untitled campaign',
      description: description.trim(),
      status: campaignStatus,
      stats: { sent: 0, replies: 0 },
      statusCounts: emptyLeadStatusCounts(),
      leadPreferences: prefs,
      leadLimit: parsedLeadLimit(),
      sequence,
      variableFallbacks,
      executionMethod: execMethod,
      unipileConfig: (connectedAccountIds.length || pendingAccountIds.length)
        ? { accountIds: connectedAccountIds, pendingAccountIds }
        : undefined,
      sendingWindow: execMethod === 'proxy' ? sendingWindow : undefined,
      workspaceId: '',
      createdBy: String(user._id),
      createdByName: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [
    activeCampaignId, user, name, description, campaignStatus, prefs, leadLimit,
    sequence, variableFallbacks, execMethod, connectedAccountIds, pendingAccountIds, sendingWindow,
  ]);

  useEffect(() => {
    if (wizardStep !== CAMPAIGN_WIZARD_STEP_RECIPIENTS || !name.trim()) return;
    let cancelled = false;
    void saveWizardDraft().then(savedId => {
      // Contacts enroll against an id. Leave /campaigns/new as soon as that id exists,
      // so a refresh reopens this draft instead of a blank wizard.
      if (cancelled || suppressDraftRedirectRef.current || !savedId || isEdit) return;
      pinDraftInUrl(savedId, CAMPAIGN_WIZARD_STEP_RECIPIENTS);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, leadLimit, leadSource]);

  /** Keep the create flow on /campaigns/new, with the draft id in the query. */
  function pinDraftInUrl(savedId: string, step: number) {
    if (isEdit || suppressDraftRedirectRef.current) return;
    const params = new URLSearchParams(location.search);
    const stepInUrl = step > 0 ? String(step) : null;
    const sameDraft = params.get('draft') === savedId;
    const sameStep = stepInUrl ? params.get('wizardStep') === stepInUrl : !params.has('wizardStep');
    if (sameDraft && sameStep) return;
    params.set('draft', savedId);
    if (stepInUrl) params.set('wizardStep', stepInUrl);
    else params.delete('wizardStep');
    const search = params.toString();
    navigate({ pathname: AppRoutes.CAMPAIGN_NEW, search: search ? `?${search}` : '' }, { replace: true });
  }

  async function saveWizardDraft(): Promise<string | null> {
    const run = () => persistWizardDraft();
    const queued = saveQueueRef.current.then(run, run);
    saveQueueRef.current = queued.then(() => undefined, () => undefined);
    return queued;
  }

  async function persistWizardDraft(): Promise<string | null> {
    if (!name.trim()) return null;
    let payload = buildCampaignPatch();
    // A paused save always includes the sequence. An empty message must not
    // replace the stored one when the save came from another step or from Close.
    if (sequenceTextOnly && !isSequenceValid(sequence)) {
      const { sequence: _sequence, ...rest } = payload;
      payload = rest;
    }
    const existingId = activeCampaignId ?? (isEdit ? id! : null);
    if (existingId) {
      const data = await updateCampaign(existingId, payload);
      return data?.campaign ? existingId : null;
    }

    if (!draftCreateRef.current) {
      draftCreateRef.current = (async () => {
        const created = await createCampaign({
          name: name.trim(),
          description: description.trim(),
          executionMethod: execMethod,
        });
        if (!created?.campaign) return null;
        const campaignId = created.campaign._id as string;
        setActiveCampaignId(campaignId);
        await flushDeferredAccounts(campaignId);
        return campaignId;
      })();
    }

    const campaignId = await draftCreateRef.current;
    if (!campaignId) {
      draftCreateRef.current = null;
      return null;
    }
    const updated = await updateCampaign(campaignId, payload);
    return updated?.campaign ? campaignId : campaignId;
  }

  saveLatestRef.current = saveWizardDraft;

  const connectedAccountKey = connectedAccountIds.join(',');
  const pendingAccountKey = pendingAccountIds.join(',');

  // Name (and every other field) is written to the draft shortly after it changes.
  // The first non-empty name creates the row; after that this only PATCHes.
  // Running campaigns stay on the explicit Save button.
  useEffect(() => {
    if (loading) return;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    if (!name.trim()) return;
    if (isEdit && campaignStatus !== 'draft') return;

    autosaveDirtyRef.current = true;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      autosaveDirtyRef.current = false;
      if (suppressDraftRedirectRef.current) return;
      void saveWizardDraft().then(savedId => {
        if (cancelled || suppressDraftRedirectRef.current || !savedId || isEdit) return;
        pinDraftInUrl(savedId, wizardStep);
      });
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading, isEdit, campaignStatus, wizardStep, name, description, leadLimit, leadSource,
    prefs, sequence, emailConfig, variableFallbacks, sendingWindow, execMethod,
    connectedAccountKey, pendingAccountKey, suppressionListIds,
  ]);

  // Debounce waits out keystrokes. Leaving before it fires still writes the latest fields.
  useEffect(() => () => {
    if (!autosaveDirtyRef.current || suppressDraftRedirectRef.current) return;
    autosaveDirtyRef.current = false;
    void saveLatestRef.current();
  }, []);

  async function handleWizardClose() {
    suppressDraftRedirectRef.current = true;
    if (name.trim()) {
      setSaving(true);
      const savedId = await saveWizardDraft();
      setSaving(false);
      if (savedId) {
        setToast({
          type: 'success',
          message: isEdit
            ? 'Campaign saved.'
            : 'Campaign saved as draft. Resume anytime from the Campaigns list.',
        });
      }
    }
    if (isEdit && id) {
      navigate(`/campaigns/${id}/leads`);
    } else {
      navigate(AppRoutes.CAMPAIGNS);
    }
  }


  const leadSourceLabel = (): string => {
    switch (leadSource) {
      case 'leadsearch': return `LeadSearch — up to ${parsedLeadLimit()} leads${wizardEnrolledTotal > 0 ? ` · ${wizardEnrolledTotal} added` : ''}`;
      case 'contacts': return `LeadDelta Contacts${wizardEnrolledTotal > 0 ? ` · ${wizardEnrolledTotal} added` : ''}`;
      case 'csv': return `CSV import${wizardEnrolledTotal > 0 ? ` · ${wizardEnrolledTotal} added` : ''}`;
      case 'posts': return `Post URL — LinkedIn post commenters${wizardEnrolledTotal > 0 ? ` · ${wizardEnrolledTotal} added` : ''}`;
      case 'sn_lists': return `Sales Navigator${wizardEnrolledTotal > 0 ? ` · ${wizardEnrolledTotal} added` : ''}`;
      case 'empty': return 'Custom campaign — no leads on launch';
      default: return '—';
    }
  };

  const navigateAfterCreate = (campaignId: string) => {
    const state: { showStartPanel?: boolean } = {};
    if (leadSource !== 'empty') {
      state.showStartPanel = true;
      setToast({ type: 'success', message: 'Campaign created and leads imported. Review and start when ready.' });
    } else {
      setToast({ type: 'success', message: 'Campaign saved as draft. Start it now or add leads first — either way, outreach begins once leads are in.' });
    }
    navigate(`/campaigns/${campaignId}/leads`, { state });
  };

  function validateForm(): string | null {
    if (!name.trim()) return 'Enter a campaign name.';
    if ((!isEdit || sequenceTextOnly) && !isSequenceValid(sequence)) {
      return 'Fill in the required AI prompt or template for message, comment, or connection-note steps.';
    }
    /*
      Missing fallbacks no longer block.

      A fallback is what a variable renders to when the lead has nothing — worth
      setting, but not worth refusing to save a campaign over. Most leads do have a
      first name, so this demanded work for a case that often never arrives, at the
      exact moment someone was trying to finish. The chips under each field still
      show which variables have no fallback.
    */
    if (!isEdit && leadSource !== 'empty' && wizardEnrolledTotal === 0) {
      return 'Add at least one lead before starting the campaign.';
    }
    if (!isEdit && execMethod === 'proxy'
        && connectedAccountIds.length === 0 && pendingAccountIds.length === 0) {
      return NO_ACCOUNT_MSG;
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Only the review step's explicit submit button may create/launch the campaign.
    // Guards against stray buttons inside embedded sub-components (filters, upload,
    // tabs) implicitly submitting the form and navigating away mid-wizard.
    if (wizardStep !== WIZARD_LAST_STEP) return;
    void handleLaunchAction('leads');
  }

  async function handleLaunchAction(afterCreate: 'leads' | 'campaigns-list', start = false) {
    const err = validateForm();
    if (err) {
      if (sequenceTextOnly && !isSequenceValid(sequence)) {
        setShowSequenceErrors(true);
        setWizardStep(CAMPAIGN_WIZARD_STEP_SEQUENCE);
      }
      setToast({ type: 'error', message: err });
      return;
    }
    continueIfVariablesOk(() => {
      void executeSubmit(afterCreate, start);
    });
  }

  async function executeSubmit(afterCreate: 'leads' | 'campaigns-list' = 'leads', start = false) {
    suppressDraftRedirectRef.current = true;
    setSaving(true);
    await saveQueueRef.current;
    let existingId = activeCampaignId ?? (isEdit ? id! : null);
    if (!existingId && draftCreateRef.current) existingId = await draftCreateRef.current;

    if (existingId) {
      const data = await updateCampaign(existingId, buildCampaignPatch());
      if (!data?.campaign) {
        setSaving(false);
        setToast({ type: 'error', message: isEdit
          ? 'Could not update campaign.'
          : 'Campaign saved, but settings could not be saved — check the sending window timezone, then save again.' });
        if (!isEdit) navigate(`/campaigns/${existingId}/edit`, { replace: true });
        return;
      }

      setSaving(false);

      if (isEdit) {
        setToast({ type: 'success', message: 'Campaign updated.' });
        navigate(`/campaigns/${existingId}/leads`);
      } else if (afterCreate === 'campaigns-list') {
        setToast({ type: 'success', message: 'Campaign saved as draft.' });
        navigate(AppRoutes.CAMPAIGNS);
      } else if (start) {
        await startCampaignAndNavigate(existingId);
      } else {
        navigateAfterCreate(existingId);
      }

    } else {
      const created = await createCampaign({
        name: name.trim(),
        description: description.trim(),
        executionMethod: execMethod,
      });
      if (!created?.campaign) {
        suppressDraftRedirectRef.current = false;
        setSaving(false);
        setToast({ type: 'error', message: 'Could not create campaign.' });
        return;
      }
      const campaignId = created.campaign._id;
      setActiveCampaignId(campaignId);
      await flushDeferredAccounts(campaignId);
      const updated = await updateCampaign(campaignId, buildCampaignPatch());
      if (!updated?.campaign) {
        setSaving(false);
        setToast({ type: 'error', message: 'Campaign created, but settings could not be saved — check the sending window timezone, then save again.' });
        navigate(`/campaigns/${campaignId}/edit`, { replace: true });
        return;
      }

      setSaving(false);

      if (afterCreate === 'campaigns-list') {
        setToast({ type: 'success', message: 'Campaign saved as draft.' });
        navigate(AppRoutes.CAMPAIGNS);
      } else if (start) {
        await startCampaignAndNavigate(campaignId);
      } else {
        navigateAfterCreate(campaignId);
      }
    }
  }

  // The wizard's "Start campaign" button should actually launch the campaign, not just
  // drop the user on the leads page with the start panel. Activation is a separate
  // request because the backend rejects a sequence update once status !== 'draft'.
  async function startCampaignAndNavigate(campaignId: string) {
    // Last gate before going live. Starting a campaign whose email step has no
    // mailbox behind it would look like it's running while silently skipping that
    // step for every lead — worse than refusing to start.
    const senderErr = emailSenderError();
    if (senderErr) {
      setToast({ type: 'error', message: senderErr });
      navigate(`/campaigns/${campaignId}/edit`, { replace: true });
      return;
    }

    setSaving(true);
    const started = await updateCampaign(campaignId, { status: 'active' });
    setSaving(false);
    if (started?.campaign?.status === 'active') {
      setToast({ type: 'success', message: 'Campaign is live — outreach has started.' });
      navigate(`/campaigns/${campaignId}/leads`);
    } else {
      setToast({ type: 'error', message: (started as any)?.message || 'Campaign saved, but could not start — review and start from the campaign page.' });
      navigate(`/campaigns/${campaignId}/leads`, { state: { showStartPanel: true } });
    }
  }

  function validateWizardStep(step: number): string | null {
    if (step === CAMPAIGN_WIZARD_STEP_SETTINGS) {
      if (!name.trim()) return 'Enter a campaign name.';
      if (execMethod === 'proxy' && connectedAccountIds.length === 0 && pendingAccountIds.length === 0) {
        return NO_ACCOUNT_MSG;
      }
      return null;
    }
    if (step === CAMPAIGN_WIZARD_STEP_RECIPIENTS) {
      if (leadSource === 'empty') return null;
      if (!isEdit && wizardEnrolledTotal === 0) {
        return 'Add at least one lead before continuing.';
      }
      return null;
    }
    if (step === CAMPAIGN_WIZARD_STEP_SEQUENCE) {
      if (sequenceLocked && !sequenceTextOnly) return null;
      if (sequenceTextOnly) {
        if (!isSequenceValid(sequence)) {
          return 'Fill in the required AI prompt or template for message, comment, or connection-note steps.';
        }
        return null;
      }
      if (!sequence.length) return 'Add at least one outreach step.';
      if (!isSequenceValid(sequence)) {
        return 'Fill in the required AI prompt or template for message, comment, or connection-note steps.';
      }
      // No fallback check here either — see validateForm.
      // An email step with no working mailbox behind it would silently skip for
      // every lead — block here rather than let the campaign start and do nothing.
      const senderErr = emailSenderError();
      if (senderErr) return senderErr;
      return null;
    }
    return null;
  }

  /**
   * Why the sequence's email step can't run yet, or null when it can.
   * Mirrors SenderIdentityService.validateCampaignIdentities on the backend, which
   * enforces the same rule at activation.
   */
  function emailSenderError(): string | null {
    if (!sequenceHasEmailStep) return null;

    // The list is already scoped to this campaign's senders — derived from its
    // LinkedIn accounts, or its creator for extension campaigns. Nothing to select.
    const selected = senderIdentities;
    if (!selected.length) {
      return execMethod === 'proxy'
        ? 'This sequence sends an email, but the campaign has no LinkedIn account to send as.'
        : 'This sequence sends an email — add yourself as a sender under "Who sends these emails?".';
    }

    const withMailbox = selected.filter(i => i.mail.connected);
    if (!withMailbox.length) {
      const names = selected.map(i => i.name || 'Sender').join(', ');
      return `Connect a mailbox for ${names} — an email step can't run without one.`;
    }

    const ready = withMailbox.filter(i => i.emailReady);
    if (!ready.length) {
      const blocked = withMailbox[0];
      return blocked.mail.pausedAt
        ? `${blocked.name || 'Sender'}'s mailbox is paused. Resume it before continuing.`
        : `${blocked.name || 'Sender'}'s mailbox needs reconnecting before continuing.`;
    }

    return null;
  }

  /**
   * Point the user at what's actually missing on the settings step: mark the field
   * red and scroll it into view. A toast on its own is easy to miss, and says
   * nothing about where to look — especially for the account picker, which sits
   * inside a collapsible section further down the form.
   */
  /**
   * Whether the sequence's required fields should show as invalid.
   *
   * False until an attempt to continue fails, so opening a half-written campaign does
   * not paint every empty field red before anything has been asked of the user.
   */
  const [showSequenceErrors, setShowSequenceErrors] = useState(false);
  const [badVariableTokens, setBadVariableTokens] = useState<string[]>([]);
  const afterVariableWarnRef = useRef<(() => void) | null>(null);

  function flagSettingsErrors() {
    const missingName = !name.trim();
    const missingAccount = execMethod === 'proxy'
      && connectedAccountIds.length === 0 && pendingAccountIds.length === 0;

    setNameError(missingName);
    setAccountError(missingAccount);
    if (missingAccount) setAccountsOpen(true);

    // Name first — it is the field higher up the form. Scrolling waits a frame so
    // the account section has expanded and settled at its final offset.
    requestAnimationFrame(() => {
      const target = missingName ? nameFieldRef.current
        : missingAccount ? accountSectionRef.current
          : null;
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  function continueIfVariablesOk(proceed: () => void) {
    if (wizardStep !== CAMPAIGN_WIZARD_STEP_SEQUENCE) {
      proceed();
      return;
    }
    const tokens = unrecognizedSequenceVariableTokens(sequence);
    if (!tokens.length) {
      proceed();
      return;
    }
    afterVariableWarnRef.current = proceed;
    setBadVariableTokens(tokens);
  }

  function confirmBadVariables() {
    const proceed = afterVariableWarnRef.current;
    afterVariableWarnRef.current = null;
    setBadVariableTokens([]);
    proceed?.();
  }

  function dismissBadVariables() {
    afterVariableWarnRef.current = null;
    setBadVariableTokens([]);
  }

  function goNextWizard() {
    const err = validateWizardStep(wizardStep);
    if (err) {
      if (wizardStep === CAMPAIGN_WIZARD_STEP_SETTINGS) flagSettingsErrors();
      // Same idea as flagSettingsErrors, for the step that has fields of its own.
      if (wizardStep === CAMPAIGN_WIZARD_STEP_SEQUENCE) setShowSequenceErrors(true);
      setToast({ type: 'error', message: err });
      return;
    }
    continueIfVariablesOk(() => {
      // Persist sequence when leaving the outreach step so a later Save can't race
      // with an older draft that still has the default CR + Message template.
      if (wizardStep === CAMPAIGN_WIZARD_STEP_SEQUENCE && name.trim()) {
        void saveWizardDraft();
      }
      setWizardStep(s => nextWizardStep(s));
    });
  }

  function goBackWizard() {
    setWizardStep(s => prevWizardStep(s));
  }

  /** An Edit link on the review: open that step, and come back here from it. */
  function editFromReview(step: number) {
    setReturnToReview(true);
    setWizardStep(step);
  }

  /*
    Save on a step opened from the review: persist, then straight back to the review.

    Every step between here and the review is checked, not just this one — an edit
    can break a later step (dropping the LinkedIn account an email step sends as), and
    the review must never be reached past a step that would refuse Next. The first one
    that fails is opened, still in this mode, so fixing it is one more Save.
  */
  async function saveAndReturnToReview() {
    for (let s = wizardStep; s < WIZARD_LAST_STEP; s++) {
      const err = validateWizardStep(s);
      if (err) {
        if (s === CAMPAIGN_WIZARD_STEP_SETTINGS) flagSettingsErrors();
        if (s === CAMPAIGN_WIZARD_STEP_SEQUENCE) setShowSequenceErrors(true);
        setToast({ type: 'error', message: err });
        setWizardStep(s);
        return;
      }
    }

    continueIfVariablesOk(() => {
      void (async () => {
        setSaving(true);
        const savedId = await saveWizardDraft();
        setSaving(false);
        if (!savedId) {
          setToast({ type: 'error', message: 'Could not save the campaign. Try again.' });
          return;
        }
        setWizardStep(WIZARD_LAST_STEP);
      })();
    });
  }

  // However the review is reached, the Edit round trip is over.
  useEffect(() => {
    if (wizardStep === WIZARD_LAST_STEP) setReturnToReview(false);
  }, [wizardStep]);

  // The recipients step is never skipped, not even for a custom ("empty") campaign:
  // skipping it made "Max leads to import" — which lives only on this step —
  // unreachable, so a campaign that hit its lead ceiling could not be raised.
  // For 'empty' the step renders a callout that leads back to the source picker.
  function nextWizardStep(s: number): number {
    return Math.min(s + 1, WIZARD_LAST_STEP);
  }

  function prevWizardStep(s: number): number {
    return Math.max(s - 1, 0);
  }

  async function handleEditSaveAndExit() {
    const sequenceErr = sequenceTextOnly && !isSequenceValid(sequence)
      ? 'Fill in the required AI prompt or template for message, comment, or connection-note steps.'
      : null;
    const err = sequenceErr ?? validateWizardStep(wizardStep) ?? (!name.trim() ? 'Enter a campaign name.' : null);
    if (err) {
      if (wizardStep === CAMPAIGN_WIZARD_STEP_SETTINGS) flagSettingsErrors();
      if (sequenceErr || wizardStep === CAMPAIGN_WIZARD_STEP_SEQUENCE) setShowSequenceErrors(true);
      if (sequenceErr) setWizardStep(CAMPAIGN_WIZARD_STEP_SEQUENCE);
      setToast({ type: 'error', message: err });
      return;
    }

    continueIfVariablesOk(() => {
      void (async () => {
        setSaving(true);
        const existingId = activeCampaignId ?? id!;
        const savedId = await saveWizardDraft();
        if (!savedId) {
          setSaving(false);
          setToast({ type: 'error', message: 'Could not update campaign.' });
          return;
        }

        setSaving(false);
        setToast({ type: 'success', message: 'Campaign saved.' });
        navigate(`/campaigns/${existingId}/leads`);
      })();
    });
  }

  function handleWizardStepClick(index: number) {
    if (index === wizardStep) return;
    if (index < wizardStep) {
      setWizardStep(index);
      return;
    }
    for (let s = wizardStep; s < index; s++) {
      const err = validateWizardStep(s);
      if (err) {
        if (s === CAMPAIGN_WIZARD_STEP_SETTINGS) flagSettingsErrors();
        if (s === CAMPAIGN_WIZARD_STEP_SEQUENCE) setShowSequenceErrors(true);
        setToast({ type: 'error', message: err });
        setWizardStep(s);
        return;
      }
    }
    continueIfVariablesOk(() => setWizardStep(index));
  }

  const sequenceOk = isSequenceValid(sequence);
  // A proxy campaign can't run without a LinkedIn account. Require one to be in the
  // pool (connected or pending 2FA). The user must connect an account first.
  const needsProxyAccount = !isEdit && execMethod === 'proxy'
    && connectedAccountIds.length === 0 && pendingAccountIds.length === 0;
  const needsLeadsForCreate = !isEdit && leadSource !== 'empty';
  const saveDisabled = saving || !name.trim()
    || (needsLeadsForCreate && wizardEnrolledTotal === 0)
    || (!isEdit && !sequenceOk)
    || needsProxyAccount;

  // Why is the save/create button disabled? Shown next to the buttons so it's obvious.
  const missingSequence = !isEdit && !sequenceOk;
  const emptySequence = !isEdit && sequence.length === 0;
  const disabledReason = saving
    ? ''
      : !name.trim()
      ? 'Enter a campaign name.'
      : needsLeadsForCreate && wizardEnrolledTotal === 0 && missingSequence
        ? 'Add leads and at least one outreach step.'
        : needsLeadsForCreate && wizardEnrolledTotal === 0
          ? 'Add at least one lead on the Choose recipients step.'
          : emptySequence
            ? 'Add at least one outreach step — e.g. Message only for existing LD Contacts.'
            : missingSequence
            ? 'Fill in the required AI prompt or template for message/comment steps.'
            : needsProxyAccount
              ? 'Connect a LinkedIn account — the campaign needs one to send from.'
              : '';

  const editSaveBlockReason = saving
    ? ''
    : !name.trim()
      ? 'Enter a campaign name.'
      : (validateWizardStep(wizardStep) ?? '');
  const editSaveDisabled = saving || !!editSaveBlockReason;

  // ── Render ─────────────────────────────────────────────────────────────────

  const inWizard = true;
  const showSettings   = !inWizard || wizardStep === CAMPAIGN_WIZARD_STEP_SETTINGS;
  const showRecipients = !inWizard || wizardStep === CAMPAIGN_WIZARD_STEP_RECIPIENTS;
  const showSequence   = !inWizard || wizardStep === CAMPAIGN_WIZARD_STEP_SEQUENCE;
  const showReview     = inWizard && wizardStep === CAMPAIGN_WIZARD_STEPS.length - 1;

  const execLabel = execMethod === 'extension' ? 'Chrome Extension' : 'LeadDelta Cloud';

  // Every workspace account that still exists on the provider — the picker shows all of
  // them and marks the ones already in this campaign as checked.
  const pickerAccounts = workspaceAccounts
    .filter(a => a.status !== 'not_found')
    .map(a => ({ accountId: a.accountId, name: a.name, email: a.email, status: a.status }));

  // The campaign's pool: connected accounts first, then the ones awaiting verification.
  const poolAccounts = [
    ...connectedAccountIds.map(accId => ({
      accId,
      pending: false,
      status: accountDetails[accId]?.status
        ?? workspaceAccounts.find(a => a.accountId === accId)?.status
        ?? 'connected',
    })),
    ...pendingAccountIds.map(accId => ({
      accId,
      pending: true,
      status: accountDetails[accId]?.status === 'not_found' ? 'not_found' : 'pending',
    })),
  ];
  const poolAccountIds = poolAccounts.map(a => a.accId);

  // The login email lives on the workspace mapping, not on the campaign pool, so look
  // it up from whichever source has it.
  const accountEmail = (accId: string): string | null =>
    accountDetails[accId]?.email
    ?? workspaceAccounts.find(a => a.accountId === accId)?.email
    ?? null;

  // Same fallback for the display name: live details only exist once the campaign
  // does, so an account selected during creation is named from the workspace list.
  const accountName = (accId: string): string | null =>
    accountDetails[accId]?.name
    ?? workspaceAccounts.find(a => a.accountId === accId)?.name
    ?? null;

  /** True when the LinkedIn login email matches the signed-in LeadDelta user's. */
  const isOwnAccount = (email: string | null) =>
    !!email && !!user?.email && email.trim().toLowerCase() === user.email.trim().toLowerCase();

  const hasSnMessageStep = sequence.some(s => s.type === 'sn_message');
  const inMailCreditSummary = useMemo(
    () => (execMethod === 'proxy' ? summarizeInMailCredits(Object.values(accountDetails)) : null),
    [execMethod, accountDetails],
  );
  const inMailCreditsFetchError = useMemo(() => {
    if (execMethod !== 'proxy' || inMailCreditSummary) return null;
    const connected = Object.values(accountDetails).filter(a => a.status === 'connected' && !a.pending);
    const err = connected.map(a => a.inMailCredits?.error).find(Boolean);
    return err || null;
  }, [execMethod, inMailCreditSummary, accountDetails]);


  const scheduleSummary = sendingWindow.enabled
    ? `${DAY_OPTIONS.filter(d => sendingWindow.days.includes(d)).join(', ') || 'No days'} · ${fmtHour(sendingWindow.startHour)}–${fmtHour(sendingWindow.endHour)}`
    : 'Runs anytime';

  return (
    <PageContainer>
      <PageHeader>
        <BackBtn type="button" onClick={() => navigate(isEdit && id ? `/campaigns/${id}/leads` : AppRoutes.CAMPAIGNS)}>
          <SvgFragment type="arrowLeft" />
          Campaigns
        </BackBtn>
        <Text $heading3 $bold style={{ marginTop: '8px' }}>
          {isEdit ? 'Edit campaign' : 'New campaign'}
        </Text>
      </PageHeader>

      {loading ? (
        <FormLoading aria-busy="true" aria-label="Loading campaign">
          <LoadingSpinner height="28px" width="28px" />
        </FormLoading>
      ) : (
        <FormCard>
          {inWizard && (
            <WizardTopBar>
              <CampaignWizardStepper
                steps={CAMPAIGN_WIZARD_STEPS}
                activeStepIndex={wizardStep}
                onStepClick={handleWizardStepClick}
                allowJumpAhead={isEdit}
              />
            </WizardTopBar>
          )}

          <form onSubmit={handleSubmit}>
            <FormBody ref={formBodyRef}>
            <Column gap="24px">

              {showSettings && (
              <>
              {inWizard && (
                <WizardStepHeader>
                  <SectionTitle>Campaign settings</SectionTitle>
                  <SectionSub>Name your campaign, choose a lead source, and execution method.</SectionSub>
                </WizardStepHeader>
              )}

              <FieldsRow>
                <FormLabel ref={nameFieldRef} style={{ flex: 2 }}>
                  <LabelRow>
                    <LabelText>Name <ReqMark>*</ReqMark></LabelText>
                    {!name.trim() && <RequiredBadge>Required</RequiredBadge>}
                  </LabelRow>
                  <StyledInput
                    type="text" placeholder="e.g. Q2 LinkedIn Outreach"
                    value={name} onChange={e => setName(e.target.value)}
                    autoFocus required
                    $invalid={nameError}
                    aria-invalid={nameError}
                  />
                  {nameError && <FieldError>Enter a campaign name to continue.</FieldError>}
                </FormLabel>
                <FormLabel style={{ flex: 2 }}>
                  <LabelText>Description</LabelText>
                  <StyledInput
                    type="text" placeholder="Optional description…"
                    value={description} onChange={e => setDescription(e.target.value)}
                  />
                  {inWizard && (
                    <DescHint>
                      A detailed description pre-fills AI message instructions in the outreach sequence step.
                    </DescHint>
                  )}
                </FormLabel>
              </FieldsRow>

              <Divider />

              <CampaignRecipientsSection
                leadSource={leadSource}
                onLeadSourceChange={setLeadSource}
              />

              <Divider />

              {/* ── Execution method picker — hidden while FORCE_PROXY_EXECUTION is on ── */}
              {!FORCE_PROXY_EXECUTION && inWizard && (
                <Column gap="2px">
                  <SectionTitle>Where should your campaign run?</SectionTitle>
                  <SectionSub>One runs on your computer. One runs in the cloud. Pick what fits how you work.</SectionSub>
                </Column>
              )}
              <Column gap="12px">
                {!FORCE_PROXY_EXECUTION && (
                  <ExecMethodRow>
                    {(['extension', 'proxy'] as CampaignExecutionMethod[]).map(m => (
                      <ExecMethodCard
                        key={m} type="button" $active={execMethod === m}
                        onClick={() => setExecMethod(m)}
                      >
                        <ExecCardTopRow>
                          <ExecMethodIcon $active={execMethod === m}>
                            {m === 'extension' ? <SvgFragment type="puzzlePiece" /> : <SvgFragment type="cloudLucide" />}
                          </ExecMethodIcon>
                          <ExecRadioDot $active={execMethod === m} aria-hidden>
                            {execMethod === m && <SvgFragment type="checkmark" />}
                          </ExecRadioDot>
                        </ExecCardTopRow>
                        <ExecMethodEyebrow>
                          {m === 'extension' ? 'Runs while online via Chrome Ext' : 'Runs whenever you want via Cloud'}
                        </ExecMethodEyebrow>
                        <ExecMethodLabel $active={execMethod === m}>
                          {m === 'extension' ? 'On your computer' : 'In the cloud 24/7'}
                        </ExecMethodLabel>
                        <ExecMethodSub>
                          {m === 'extension'
                            ? 'Runs through your Chrome Browser. Chrome must stay open and your computer awake. Close your laptop, and outreach pauses until you’re back.'
                            : 'Runs through the cloud. No browser. No extension. Campaigns run from the cloud on your schedule, yours or a teammate’s, until they’re done or paused.'}
                        </ExecMethodSub>
                        <ExecMethodBadge>
                          {m === 'extension' ? 'Browser must stay open' : 'Set & forget'}
                        </ExecMethodBadge>
                      </ExecMethodCard>
                    ))}
                  </ExecMethodRow>
                )}

                {execMethod === 'proxy' && (
                  <SettingsAccordion ref={accountSectionRef} $invalid={accountError}>
                    <AccordionHeader
                      type="button"
                      onClick={() => setAccountsOpen(o => !o)}
                      aria-expanded={accountsOpen}
                    >
                      <AccordionChevron $open={accountsOpen} aria-hidden>
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </AccordionChevron>
                      <AccordionTitle>Connect LinkedIn account</AccordionTitle>
                      {!accountsOpen && (
                        <AccordionSummary>
                          {poolAccounts.length === 0
                            ? 'No account connected'
                            : `${poolAccounts.length} account${poolAccounts.length > 1 ? 's' : ''} connected`}
                        </AccordionSummary>
                      )}
                    </AccordionHeader>

                    {accountsOpen && (
                      <AccordionBody>
                        {/* Connected accounts only — see handleAddAccount. A pending attempt
                            is not an account the trial is spending its seat on. */}
                        {trial && connectedAccountIds.length >= 1 && (
                          <ProxyHint>
                            Trial plan allows only <strong>1</strong> LinkedIn account for cloud execution. To add another, delete the existing sender in Settings → Sending accounts.
                          </ProxyHint>
                        )}

                        <Column gap="6px">
                          <AccountFieldLabel>Connect account</AccountFieldLabel>
                          <CampaignAccountPicker
                            accounts={pickerAccounts}
                            selectedIds={poolAccountIds}
                            disabled={addingAccount || !!checkpoint}
                            invalid={accountError}
                            onToggle={handleAccountPickerToggle}
                            onAddNew={() => { setReconnectingAccountId(null); setShowAccountModal(true); }}
                          />
                          {accountError && <FieldError>{NO_ACCOUNT_MSG}</FieldError>}
                        </Column>

                        {poolAccounts.length > 0 && (
                          <Column gap="6px">
                            <AccountFieldLabel>Connected account</AccountFieldLabel>
                            <AccountsList>
                              {poolAccounts.map(({ accId, pending, status }) => {
                                const label = accountName(accId);
                                const email = accountEmail(accId);
                                const badge = accountBadge(status);
                                const creditLine = !pending && status === 'connected'
                                  ? [
                                    formatAccountProducts(accountDetails[accId]?.premiumFeatures),
                                    formatAccountInMailCreditsLine(accountDetails[accId]?.inMailCredits),
                                  ].filter(Boolean).join(' · ') || null
                                  : null;
                                return (
                                  <ConnectedAccountRow key={accId}>
                                    <AccountAvatar name={label} email={email} size={32} />
                                    <Column gap="2px" style={{ flex: 1, minWidth: 0 }}>
                                      <AccountName>
                                        {label || 'LinkedIn account'}
                                        {isOwnAccount(email) && <OwnAccountTag> (Me)</OwnAccountTag>}
                                      </AccountName>
                                      <AccountMetaRow>
                                        <TooltipWrapper tooltipText={accId} placement="top">
                                          <AccountEmailText>{email || accId}</AccountEmailText>
                                        </TooltipWrapper>
                                        <AccountStatusBadge $tone={badge.tone}>{badge.label}</AccountStatusBadge>
                                      </AccountMetaRow>
                                      {creditLine && (
                                        <AccountCreditsText>{creditLine}</AccountCreditsText>
                                      )}
                                    </Column>
                                    {pending && checkpoint?.accountId !== accId && (
                                      <VerifyAccountBtn
                                        type="button"
                                        disabled={addingAccount}
                                        onClick={() => {
                                          setCheckpoint({ accountId: accId, type: '', campaignId: activeCampaignId ?? id! });
                                          setCheckpointCode('');
                                        }}
                                      >
                                        Verify
                                      </VerifyAccountBtn>
                                    )}
                                    {!checkpoint && !pending && canReconnect(status) && (
                                      <VerifyAccountBtn
                                        type="button"
                                        disabled={addingAccount}
                                        onClick={() => { setReconnectingAccountId(accId); setShowAccountModal(true); }}
                                      >
                                        Reconnect
                                      </VerifyAccountBtn>
                                    )}
                                    {/* Remove, not Disconnect — Disconnect got clicked instead of Next
                                        while creating a campaign and tore the session down. Remove only
                                        takes the account out of this campaign; the full teardown is
                                        "Delete sender" in Settings → Sending accounts. */}
                                    {!checkpoint && (
                                      <RemoveAccountBtn
                                        type="button"
                                        onClick={() => void handleRemoveFromCampaign(accId)}
                                        disabled={addingAccount}
                                      >
                                        Remove
                                      </RemoveAccountBtn>
                                    )}
                                  </ConnectedAccountRow>
                                );
                              })}
                            </AccountsList>
                          </Column>
                        )}

                        {/* ── 2FA checkpoint ── */}
                        {checkpoint && (
                          <CheckpointBox>
                            <ProxyConfigTitle>Verification required</ProxyConfigTitle>
                            <ProxyHint>{checkpointHint(checkpoint.type)}</ProxyHint>
                            {/* CAPTCHA / PHONE_REGISTER can't be resolved here — show guidance only */}
                            {checkpointMode(checkpoint.type) === 'blocked' ? null : (
                              <ProxyGrid>
                                {checkpointMode(checkpoint.type) === 'code' && (
                                  <ProxyLabel style={{ flex: '0 0 200px' }}>
                                    <ProxyLabelText>Verification code <ProxyRequired>*</ProxyRequired></ProxyLabelText>
                                    <StyledInput
                                      type="text"
                                      value={checkpointCode}
                                      onChange={e => setCheckpointCode(e.target.value)}
                                      placeholder="123456"
                                      autoFocus
                                    />
                                  </ProxyLabel>
                                )}
                                <ProxyLabel style={{ flex: '0 0 auto', justifyContent: 'flex-end' }}>
                                  <ProxyLabelText>&nbsp;</ProxyLabelText>
                                  <ConnectBtn
                                    type="button"
                                    disabled={
                                      addingAccount ||
                                      (checkpointMode(checkpoint.type) === 'code' && !checkpointCode.trim())
                                    }
                                    onClick={() => void handleCheckpointSubmit()}
                                  >
                                    {addingAccount
                                      ? 'Verifying…'
                                      : checkpointMode(checkpoint.type) === 'approve'
                                        ? "I've approved — Continue"
                                        : 'Verify'}
                                  </ConnectBtn>
                                </ProxyLabel>
                              </ProxyGrid>
                            )}
                            {/* Reopening a pending account from the list loses the original
                                checkpoint type, so the UI defaults to asking for a code. If it
                                was actually an in-app approval, let the user skip straight to
                                the no-code check instead of forcing them to type something. */}
                            {checkpointMode(checkpoint.type) === 'code' && !checkpoint.type && (
                              <CheckpointAltLink
                                type="button"
                                disabled={addingAccount}
                                onClick={() => void handleCheckpointSubmit(true)}
                              >
                                Already approved this in the LinkedIn app? Continue without a code
                              </CheckpointAltLink>
                            )}
                            {/* The way out when the verification is not going to finish —
                                see cancelCheckpoint. Shown for every type, including the
                                ones that cannot be completed here at all. */}
                            <CheckpointAltLink
                              type="button"
                              disabled={addingAccount}
                              onClick={() => void cancelCheckpoint()}
                            >
                              Cancel and start over
                            </CheckpointAltLink>
                          </CheckpointBox>
                        )}
                      </AccordionBody>
                    )}
                  </SettingsAccordion>
                )}

                {execMethod === 'proxy' && (
                  <SettingsAccordion>
                    <AccordionHeader
                      type="button"
                      onClick={() => setScheduleOpen(o => !o)}
                      aria-expanded={scheduleOpen}
                    >
                      <AccordionChevron $open={scheduleOpen} aria-hidden>
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </AccordionChevron>
                      <AccordionTitle>Sending schedule</AccordionTitle>
                      <AccordionSummary>{scheduleSummary}</AccordionSummary>
                    </AccordionHeader>

                    {scheduleOpen && (
                      <AccordionBody>
                        <ScheduleModeRow>
                          <ScheduleModeCard
                            type="button"
                            $active={sendingWindow.enabled}
                            onClick={() => setSendingWindow(w => ({ ...w, enabled: true }))}
                          >
                            <ScheduleRadio $active={sendingWindow.enabled} aria-hidden />
                            <Column gap="3px">
                              <ScheduleModeTitle $active={sendingWindow.enabled}>Restrict to schedule</ScheduleModeTitle>
                              <ScheduleModeSub>Send messages only during selected days and hours.</ScheduleModeSub>
                            </Column>
                          </ScheduleModeCard>
                          <ScheduleModeCard
                            type="button"
                            $active={!sendingWindow.enabled}
                            onClick={() => setSendingWindow(w => ({ ...w, enabled: false }))}
                          >
                            <ScheduleRadio $active={!sendingWindow.enabled} aria-hidden />
                            <Column gap="3px">
                              <ScheduleModeTitle $active={!sendingWindow.enabled}>Run anytime</ScheduleModeTitle>
                              <ScheduleModeSub>Send messages 24/7 without restrictions.</ScheduleModeSub>
                            </Column>
                          </ScheduleModeCard>
                        </ScheduleModeRow>

                        {sendingWindow.enabled && (
                          <>
                            <SendingWindowGrid>
                              <ProxyLabel style={{ flex: '1 1 240px', maxWidth: 340 }}>
                                <ProxyLabelText>Timezone</ProxyLabelText>
                                <StyledSelect
                                  value={sendingWindow.timezone}
                                  onChange={e => setSendingWindow(w => ({ ...w, timezone: e.target.value }))}
                                >
                                  {!ALL_TIMEZONES.includes(sendingWindow.timezone) && !COMMON_TIMEZONES.includes(sendingWindow.timezone) && (
                                    <option value={sendingWindow.timezone}>{sendingWindow.timezone}</option>
                                  )}
                                  <optgroup label="Common">
                                    {COMMON_TIMEZONES.map(tz => (
                                      <option key={`c-${tz}`} value={tz}>{formatTimezoneLabel(tz)}</option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="All timezones">
                                    {ALL_TIMEZONES.map(tz => (
                                      <option key={tz} value={tz}>{formatTimezoneLabel(tz)}</option>
                                    ))}
                                  </optgroup>
                                </StyledSelect>
                              </ProxyLabel>

                              <ProxyLabel style={{ flex: '0 0 auto', minWidth: 0 }}>
                                <ProxyLabelText>Active hours</ProxyLabelText>
                                <HourRangeRow>
                                  <StyledSelect
                                    style={{ width: 96 }}
                                    value={sendingWindow.startHour}
                                    onChange={e => setSendingWindow(w => ({ ...w, startHour: parseInt(e.target.value, 10) }))}
                                  >
                                    {Array.from({ length: 24 }, (_, h) => (
                                      <option key={h} value={h} disabled={h >= sendingWindow.endHour}>{fmtHour(h)}</option>
                                    ))}
                                  </StyledSelect>
                                  <HourSep>—</HourSep>
                                  <StyledSelect
                                    style={{ width: 96 }}
                                    value={sendingWindow.endHour}
                                    onChange={e => setSendingWindow(w => ({ ...w, endHour: parseInt(e.target.value, 10) }))}
                                  >
                                    {Array.from({ length: 24 }, (_, i) => i + 1).map(h => (
                                      <option key={h} value={h} disabled={h <= sendingWindow.startHour}>{fmtHour(h)}</option>
                                    ))}
                                  </StyledSelect>
                                </HourRangeRow>
                              </ProxyLabel>
                            </SendingWindowGrid>

                            <Column gap="6px">
                              <ProxyLabelText>Days</ProxyLabelText>
                              <SendingWindowDaysRow>
                                {DAY_OPTIONS.map(day => (
                                  <DayToggle key={day} $active={sendingWindow.days.includes(day)}>
                                    <input
                                      type="checkbox"
                                      checked={sendingWindow.days.includes(day)}
                                      onChange={e => setSendingWindow(w => ({
                                        ...w,
                                        days: e.target.checked
                                          ? [...w.days, day]
                                          : w.days.filter(d => d !== day),
                                      }))}
                                    />
                                    {day}
                                  </DayToggle>
                                ))}
                              </SendingWindowDaysRow>
                            </Column>

                            {sendingWindow.days.length === 0 && (
                              <SendingSummary $warn>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" />
                                  <polyline points="12 6 12 12 16 14" />
                                </svg>
                                No days selected — the campaign will never send.
                              </SendingSummary>
                            )}
                          </>
                        )}
                      </AccordionBody>
                    )}
                  </SettingsAccordion>
                )}
              </Column>

              <Divider />

              {/* Only the owner edits a campaign (checkCampaignOwner), so the owner is this user. */}
              <CampaignSuppressionLists
                createdBy={user?._id ? String(user._id) : undefined}
                value={suppressionListIds}
                onChange={setSuppressionListIds}
              />

              </>
              )}

              {showRecipients && (
              <>
              {inWizard && (
                <WizardStepHeader>
                  <Column gap="2px">
                    <SectionTitle>Choose recipients</SectionTitle>
                    <SectionSub>
                      {leadSource === 'empty'
                        ? 'You chose a custom campaign — continue to the outreach sequence.'
                        : leadSource === 'posts'
                          ? 'Import people from a LinkedIn post’s comments, then review who’s in the campaign.'
                          : leadSource === 'sn_lists'
                            ? 'Load your Sales Navigator lead lists, pick one, then import its people into the campaign.'
                            : 'Add leads using the same tools as on the campaign details page.'}
                    </SectionSub>
                  </Column>

                </WizardStepHeader>
              )}

              {leadSource === 'empty' && (
                <RecipientsEmptyCallout>
                  <RecipientsEmptyTitle>No leads to add</RecipientsEmptyTitle>
                  <RecipientsEmptyText>
                    You chose a custom campaign. Continue to build your outreach sequence — you can save the campaign as a draft,
                    but outreach cannot start until leads are added later.
                  </RecipientsEmptyText>
                  <RecipientsEmptyAction
                    type="button"
                    onClick={() => setWizardStep(CAMPAIGN_WIZARD_STEP_SETTINGS)}
                  >
                    Pick a different recipient source
                  </RecipientsEmptyAction>
                </RecipientsEmptyCallout>
              )}

              {leadSource === 'leadsearch' && (
                <Column gap="12px" id="wizard-targeting-prefs">
                  {/* Import ceiling and filter reset live inside the targeting panel, so the
                      whole lead search reads as one card. */}
                  <CampaignLeadPreferences
                    value={prefs}
                    onChange={setPrefs}
                    required
                    leadLimit={Math.min(parsedLeadLimit(), maxLeadsPerImport)}
                    leadLimitOptions={leadLimitOptions}
                    onLeadLimitChange={setLeadLimit}
                    leadLimitTooltip={trial
                      ? `Trial accounts are limited to ${maxLeadsPerImport} leads per import.`
                      : `Up to ${maxLeadsPerImport} LeadSearch leads per campaign.`}
                    onClearFilters={() => setPrefs(EMPTY_LEAD_PREFERENCES)}
                  />
                </Column>
              )}

              {(leadSource === 'leadsearch' || leadSource === 'contacts' || leadSource === 'csv' || leadSource === 'posts' || leadSource === 'sn_lists') && (
                wizardCampaign ? (
                  <CampaignPanelBoundary>
                    <CampaignLeadsView
                      campaign={wizardCampaign}
                      embedded
                      embeddedSource={leadSource}
                      liveLeadPreferences={prefs}
                      onEnrolledTotalChange={setWizardEnrolledTotal}
                      onCampaignUpdate={updated => {
                        if (updated.leadPreferences) setPrefs(updated.leadPreferences);
                        if (updated.leadLimit != null) setLeadLimit(String(updated.leadLimit));
                      }}
                      onClose={() => {}}
                    />
                  </CampaignPanelBoundary>
                ) : (
                  <Text $paragraph2 style={{ color: styles.colors.black300 }}>Preparing campaign…</Text>
                )
              )}
              </>
              )}

              {showSequence && (
              <>
              {inWizard && (
                <WizardStepHeader>
                  <SectionTitle>Outreach sequence</SectionTitle>
                  <SectionSub>
                    {sequenceTextOnly
                      ? 'Message text can be edited. Steps, delays, and order stay as they were when the campaign started.'
                      : sequenceLocked
                        ? 'Sequence is locked while the campaign is active or stopped.'
                        : 'Pick a template or build from scratch — e.g. message-only for existing LD Contacts.'}
                  </SectionSub>
                </WizardStepHeader>
              )}

              <Column gap="8px">
                <CampaignSequenceBuilder
                  value={sequence}
                  onChange={setSequence}
                  readOnly={sequenceLocked && !sequenceTextOnly}
                  textOnly={sequenceTextOnly}
                  campaignDescription={description}
                  variableFallbacks={variableFallbacks}
                  onVariableFallbackChange={handleVariableFallbackChange}
                  senderIdentities={senderIdentities}
                  emailConfig={emailConfig}
                  onEmailConfigChange={patch => setEmailConfig(prev => ({ ...prev, ...patch }))}
                  showErrors={showSequenceErrors}
                  inMailCreditSummary={execMethod === 'proxy' ? inMailCreditSummary : null}
                />

                {/*
                  Senders appear only once the sequence actually sends email. A
                  LinkedIn-only campaign never has to think about mailboxes — the
                  requirement is enforced at activation, not at connect time.
                */}
                {/* Side by side: who it goes out as, and who it can reach. Both are
                    prerequisites for the same step, and stacked they pushed the
                    sequence itself off the screen. */}
                {sequenceHasEmailStep && (
                  <EmailSetupRow>
                    <CampaignSenderIdentities
                      campaignId={activeCampaignId ?? undefined}
                      requiresEmail
                      executionMethod={execMethod}
                      onIdentitiesLoaded={setSenderIdentities}
                      showErrors={showSequenceErrors}
                      returnTo={(() => {
                        const params = new URLSearchParams(location.search);
                        if (wizardStep > 0) params.set('wizardStep', String(wizardStep));
                        if (!isEdit && activeCampaignId) params.set('draft', activeCampaignId);
                        const search = params.toString();
                        return `${location.pathname}${search ? `?${search}` : ''}`;
                      })()}
                    />
                    {activeCampaignId && (
                      <CampaignEmailCoverage
                        campaignId={activeCampaignId}
                        preference={emailStepPreference}
                        refreshKey={wizardEnrolledTotal}
                      />
                    )}
                  </EmailSetupRow>
                )}

                {/* The signature editor lives in Settings → Sending accounts. It
                    belongs to the sender and is reused across all their campaigns, so
                    editing it from one campaign implied a scope it never had. The email
                    step still shows whose signature gets appended, and links there. */}
              </Column>
              </>
              )}

              {showReview && (
                <>
                  <WizardStepHeader>
                    <SectionTitle>Campaign review</SectionTitle>
                    <SectionSub>
                      Review settings, recipients, and sequence. When ready, start the campaign to go live.
                    </SectionSub>
                  </WizardStepHeader>

                  <ReviewSection>
                    <ReviewSectionHead>
                      <ReviewSectionTitle>Settings</ReviewSectionTitle>
                      <ReviewEditLink type="button" onClick={() => editFromReview(CAMPAIGN_WIZARD_STEP_SETTINGS)}>Edit</ReviewEditLink>
                    </ReviewSectionHead>
                    <ReviewGrid>
                      <ReviewCell><ReviewLabel>Name</ReviewLabel><ReviewValue>{name.trim() || '—'}</ReviewValue></ReviewCell>
                      <ReviewCell><ReviewLabel>Description</ReviewLabel><ReviewValue>{description.trim() || '—'}</ReviewValue></ReviewCell>
                      <ReviewCell><ReviewLabel>Execution</ReviewLabel><ReviewValue>{execLabel}</ReviewValue></ReviewCell>
                      {execMethod === 'proxy' && hasSnMessageStep && (
                        <ReviewCell>
                          <ReviewLabel>InMail credits</ReviewLabel>
                          <ReviewValue>
                            {inMailCreditSummary
                              ? `${formatInMailCreditBalance(inMailCreditSummary)} · Open Profile free`
                              : inMailCreditsFetchError
                                ? `Unavailable — ${inMailCreditsFetchError}`
                                : connectedAccountIds.length
                                  ? 'Unavailable — refresh after the LinkedIn account is connected'
                                  : 'Connect a LinkedIn account to load InMail credits'}
                          </ReviewValue>
                        </ReviewCell>
                      )}
                    </ReviewGrid>
                  </ReviewSection>

                  <ReviewSection>
                    <ReviewSectionHead>
                      <ReviewSectionTitle>Recipients</ReviewSectionTitle>
                      <ReviewEditLink type="button" onClick={() => editFromReview(CAMPAIGN_WIZARD_STEP_RECIPIENTS)}>Edit</ReviewEditLink>
                    </ReviewSectionHead>
                    <ReviewValue>{leadSourceLabel()}</ReviewValue>
                  </ReviewSection>

                  <ReviewSection>
                    <ReviewSectionHead>
                      <ReviewSectionTitle>Outreach sequence</ReviewSectionTitle>
                      <ReviewEditLink type="button" onClick={() => editFromReview(CAMPAIGN_WIZARD_STEP_SEQUENCE)}>Edit</ReviewEditLink>
                    </ReviewSectionHead>
                    <ReviewValue>{formatSequenceSummaryWithDays(sequence)}</ReviewValue>
                  </ReviewSection>
                </>
              )}

            </Column>
            </FormBody>

              {inWizard && (
                <WizardFooter>
                  <CancelBtn type="button" className="footer-close-desktop" onClick={() => void handleWizardClose()} disabled={saving}>
                    {saving ? 'Saving…' : 'Cancel'}
                  </CancelBtn>

                  <WizardFooterActions>
                    {wizardStep > 0 && (
                      <CancelBtn type="button" className="footer-back-desktop" onClick={goBackWizard}>Back</CancelBtn>
                    )}

                    {wizardStep < WIZARD_LAST_STEP ? (
                      returnToReview ? (
                        /* Opened from an Edit link on the review — see saveAndReturnToReview.
                           One button, in both create and edit mode: the review is where
                           this round trip started, so it is where it ends. */
                        <FooterBtnWrap>
                          <PrimaryBtn
                            type="button"
                            disabled={saving}
                            onClick={() => void saveAndReturnToReview()}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </PrimaryBtn>
                        </FooterBtnWrap>
                      ) : isEdit ? (
                        <>
                          <TooltipWrapper tooltipText={editSaveBlockReason} placement="top">
                            <FooterBtnWrap>
                              <PrimaryBtn
                                type="button"
                                disabled={editSaveDisabled}
                                onClick={() => void handleEditSaveAndExit()}
                              >
                                {saving ? 'Saving…' : 'Save'}
                              </PrimaryBtn>
                            </FooterBtnWrap>
                          </TooltipWrapper>
                          <FooterBtnWrap>
                            <CancelBtn type="button" onClick={goNextWizard}>
                              Next step <SvgFragment type="arrowRight" />
                            </CancelBtn>
                          </FooterBtnWrap>
                        </>
                      ) : (
                        <FooterBtnWrap>
                          <PrimaryBtn type="button" onClick={goNextWizard}>
                            Next step <SvgFragment type="arrowRight" />
                          </PrimaryBtn>
                        </FooterBtnWrap>
                      )
                    ) : isEdit ? (
                      <TooltipWrapper tooltipText={disabledReason} placement="top">
                        <FooterBtnWrap>
                          <PrimaryBtn type="submit" disabled={saveDisabled}>
                            {saving ? 'Saving…' : 'Save changes'}
                          </PrimaryBtn>
                        </FooterBtnWrap>
                      </TooltipWrapper>
                    ) : (
                      <>
                        <TooltipWrapper tooltipText={disabledReason} placement="top">
                          <FooterBtnWrap>
                            <CancelBtn
                              type="button"
                              disabled={saveDisabled || saving}
                              onClick={() => void handleLaunchAction('campaigns-list')}
                            >
                              {saving ? 'Saving…' : 'Save as draft'}
                            </CancelBtn>
                          </FooterBtnWrap>
                        </TooltipWrapper>
                        <TooltipWrapper
                          tooltipText={leadSource === 'empty'
                            ? 'Starts with no leads — outreach begins as soon as you add some.'
                            : disabledReason}
                          placement="top"
                        >
                          <FooterBtnWrap>
                            <PrimaryBtn
                              type="button"
                              disabled={saveDisabled || saving}
                              onClick={() => void handleLaunchAction('leads', true)}
                            >
                              {saving ? 'Starting…' : 'Start campaign'}
                            </PrimaryBtn>
                          </FooterBtnWrap>
                        </TooltipWrapper>
                      </>
                    )}
                  </WizardFooterActions>

                  <WizardFooterBottomRow>
                    {wizardStep > 0 && (
                      <CancelBtn type="button" onClick={goBackWizard}>Back</CancelBtn>
                    )}
                    <CancelBtn type="button" onClick={() => void handleWizardClose()} disabled={saving}>
                      {saving ? 'Saving…' : 'Cancel'}
                    </CancelBtn>
                  </WizardFooterBottomRow>
                </WizardFooter>
              )}
          </form>
        </FormCard>
      )}

      <AddLinkedInAccountModal
        show={showAccountModal}
        mode={reconnectingAccountId ? 'reconnect' : 'add'}
        accountName={reconnectingAccountId ? accountDetails[reconnectingAccountId]?.name : undefined}
        submitting={addingAccount}
        onHide={closeAccountModal}
        onSubmit={handleAccountModalSubmit}
      />
      <CampaignVariableWarnModal
        show={badVariableTokens.length > 0}
        tokens={badVariableTokens}
        includeSignature={sequence.some(s => s.type === 'email')}
        onHide={dismissBadVariables}
        onConfirm={confirmBadVariables}
      />
    </PageContainer>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 0 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  box-sizing: border-box;
  ${campaignMobile`padding: 0 8px 8px;`}
`;

const PageHeader = styled.div`
  width: 100%;
  flex-shrink: 0;
  padding-top: 16px;
  padding-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FormLoading = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 60px 24px;
`;

const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer; padding: 0;
  font-family: NunitoSans; font-size: 13px; font-weight: 600; color: ${styles.colors.black400};
  .svg { width: 16px; height: 16px; }
  .path { fill: ${styles.colors.black400} !important; }
  &:hover { color: ${styles.colors.primary600}; .path { fill: ${styles.colors.primary600} !important; } }
`;

const FormCard = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${styles.colors.white};
  border: 1px solid #e8edf5;
  border-radius: 16px;
  padding: 28px 28px 0;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 28px rgba(15, 23, 42, 0.1);

  > form {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  ${campaignMobile`
    padding: 16px 12px 0;
    border-radius: 12px;
    box-shadow:
      0 1px 2px rgba(15, 23, 42, 0.04),
      0 6px 20px rgba(15, 23, 42, 0.09);
  `}
`;

const FormBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 24px;
`;

/* Negative side margins pull the rule out to the card's edges, so the stepper reads
   as a header band rather than a floating row. */
const WizardTopBar = styled.div`
  flex-shrink: 0;
  margin: -8px -28px 24px;
  padding: 8px 28px 18px;
  border-bottom: 1px solid #eef2f7;

  ${campaignMobile`
    margin: -4px -12px 16px;
    padding: 4px 12px 14px;
  `}
`;

/*
  The two email prerequisites, side by side. Equal columns rather than auto: the
  coverage panel's width swings with its numbers, and letting it size itself made
  the pair jump around as leads were added.
*/
/*
  `stretch`, not `start`: the two panels are a pair, and letting each size to its own
  content left one visibly shorter than the other depending on how many senders or
  how much coverage detail there was.
*/
const EmailSetupRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: stretch;
  ${campaignMobile`grid-template-columns: 1fr;`}

  /* The panels are the grid items, so they carry the stretch themselves. */
  > * { height: 100%; box-sizing: border-box; }
`;

const WizardStepHeader = styled.div`
  /* Step title with its subtitle stacked underneath. */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 4px;
`;

const WizardFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 -28px;
  padding: 12px 12px;
  background: ${styles.colors.white};
  border-top: 1px solid #e8edf5;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 -10px 28px rgba(15, 23, 42, 0.1);

  ${campaignMobile`
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin: 0 -12px;
    padding: 12px 12px;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 -8px 22px rgba(15, 23, 42, 0.1);

    .footer-close-desktop,
    .footer-back-desktop { display: none; }
  `}
`;

const WizardFooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  ${campaignMobile`
    width: 100%;
    flex-direction: column-reverse;
    gap: 8px;

    & > * { width: 100%; }
  `}
`;

const WizardFooterBottomRow = styled.div`
  display: none;

  ${campaignMobile`
    display: flex;
    width: 100%;
    gap: 8px;

    & > button {
      flex: 1;
      width: auto;
      min-width: 0;
    }

    & > button:only-child {
      flex: none;
      width: 100%;
    }
  `}
`;

const FooterBtnWrap = styled.span`
  display: inline-flex;
  ${campaignMobile`
    display: flex;
    width: 100%;
    & > button { width: 100%; }
  `}
`;

const ReviewSection = styled.div`
  padding: 16px 18px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: ${styles.colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover { border-color: #e1e7f3; box-shadow: 0 3px 12px rgba(20,30,80,0.05); }
`;

const ReviewSectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const ReviewSectionTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${styles.colors.black400};
  &::before {
    content: '';
    width: 6px; height: 6px; border-radius: 2px;
    background: ${styles.colors.primary600};
    flex-shrink: 0;
  }
`;

const ReviewEditLink = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 7px;
  border: 1px solid #dde3ee;
  background: ${styles.colors.white};
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 700;
  color: ${styles.colors.primary600};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover { background: ${styles.colors.primary100}; border-color: ${styles.colors.primary200}; }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px 20px;
`;

const ReviewCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewLabel = styled.span`
  font-family: NunitoSans;
  font-size: 11px;
  font-weight: 700;
  color: ${styles.colors.black300};
`;

const ReviewValue = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black600};
  line-height: 1.45;
`;

const FieldsRow = styled.div`display: flex; gap: 12px; flex-wrap: wrap;`;

const FormLabel = styled.label`display: flex; flex-direction: column; gap: 4px; min-width: 160px;`;

const LabelRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const LabelText = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.black400};
`;

const DescHint = styled.span`
  font-family: NunitoSans;
  font-size: 12px;
  color: ${styles.colors.black400};
  line-height: 1.4;
  margin-top: 2px;
`;

const ReqMark = styled.span`
  color: ${styles.colors.red600};
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 18px;
  padding: 0 9px 0 7px;
  border-radius: 20px;
  background: #fbf7ef;
  color: #9a6f22;
  border: 1px solid #ece0c9;
  font-family: NunitoSans;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  box-sizing: border-box;
  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #e0a93e;
    flex-shrink: 0;
  }
`;

const StyledInput = styled.input<{ $invalid?: boolean }>`
  height: 36px; padding: 0 12px; border-radius: 8px;
  border: 1.5px solid ${({ $invalid }) => ($invalid ? styles.colors.red600 : '#dde3ee')};
  box-shadow: ${({ $invalid }) => ($invalid ? '0 0 0 3px rgba(255,103,96,0.15)' : 'none')};
  font-size: 13px; font-family: NunitoSans; color: ${styles.colors.black600};
  background: ${styles.colors.white}; outline: none; width: 100%; box-sizing: border-box;
  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? styles.colors.red600 : styles.colors.primary600)};
    box-shadow: 0 0 0 3px ${({ $invalid }) => ($invalid ? 'rgba(255,103,96,0.15)' : 'rgba(89,100,255,0.08)')};
  }
  &::placeholder { color: ${styles.colors.black300}; }
  &:disabled {
    background: ${styles.colors.black100};
    border-color: #e6ebf3;
    color: ${styles.colors.black300};
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const FieldError = styled.span`
  font-family: NunitoSans;
  font-size: 11.5px;
  font-weight: 600;
  color: ${styles.colors.red600};
  margin-top: 2px;
`;

const Divider = styled.div`height: 1px; background: #eef2f7;`;

const SectionTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const SectionSub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
`;

const RecipientsEmptyCallout = styled.div`
  padding: 16px 18px; border-radius: 10px;
  border: 1px dashed #d0d7e6; background: #f8f9fc;
`;

const RecipientsEmptyTitle = styled.span`
  display: block; font-family: NunitoSans; font-size: 14px; font-weight: 800;
  color: ${styles.colors.black600}; margin-bottom: 6px;
`;

const RecipientsEmptyText = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400}; line-height: 1.5;
`;

const RecipientsEmptyAction = styled.button`
  display: block; margin-top: 10px; padding: 0; border: none; background: none;
  font-family: NunitoSans; font-size: 12px; font-weight: 700; cursor: pointer;
  color: ${styles.colors.primary600};
  &:hover { text-decoration: underline; }
`;

const PrimaryBtn = styled.button`
  height: 36px; padding: 0 20px; background: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 13px; font-weight: 700; font-family: NunitoSans; cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  .svg { width: 14px; height: 14px; }
  .path { fill: ${styles.colors.white} !important; }
  &:hover:not(:disabled) { background: ${styles.colors.primary400}; }
  &:disabled { background: ${styles.colors.black200}; cursor: default; }
  ${campaignMobile`
    height: 40px;
    justify-content: center;
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
  `}
`;

const CancelBtn = styled.button`
  height: 36px; padding: 0 16px; background: transparent; color: ${styles.colors.black400};
  border: 1.5px solid #dde3ee; border-radius: 8px; font-size: 13px; font-weight: 600;
  font-family: NunitoSans; cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  .svg { width: 14px; height: 14px; }
  .path { fill: ${styles.colors.black400} !important; }
  &:hover { background: ${styles.colors.black100}; }
  ${campaignMobile`
    height: 40px;
    justify-content: center;
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
  `}
`;

const ExecMethodRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 840px) { grid-template-columns: 1fr; }
`;

const ExecMethodCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 17px;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  min-height: 130px;
  border: 1px solid ${({ $active }) => $active ? styles.colors.primary600 : '#e1e5eb'};
  background: ${({ $active }) => $active ? 'rgba(89,100,255,0.05)' : styles.colors.white};
  box-shadow: ${({ $active }) => $active
    ? '0px 0px 0px 0px rgba(89,100,255,0.2), 0px 1px 3px rgba(0,0,0,0.10), 0px 1px 2px rgba(0,0,0,0.10)'
    : 'none'};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover:not(:disabled) { border-color: ${styles.colors.primary600}; box-shadow: 0px 1px 3px rgba(0,0,0,0.08), 0px 1px 2px rgba(0,0,0,0.08); }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const ExecCardTopRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ExecMethodIcon = styled.span<{ $active?: boolean }>`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 40px; height: 40px; border-radius: 10px;
  background: ${({ $active }) => $active ? 'rgba(89,100,255,0.10)' : '#e7edff'};
  .svg { width: 20px; height: 20px; }
  .path { stroke: ${styles.colors.primary600}; fill: none; }
`;

const ExecRadioDot = styled.div<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 2px solid ${({ $active }) => $active ? styles.colors.primary600 : '#e1e5eb'};
  background: ${({ $active }) => $active ? styles.colors.primary600 : '#fff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .svg { width: 14px; height: 14px; }
  .path { fill: #fff !important; }
`;

const ExecMethodLabel = styled.span<{ $active: boolean }>`
  font-family: NunitoSans; font-size: 14px; font-weight: 800;
  color: ${styles.colors.black600};
`;

const ExecMethodEyebrow = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 800;
  color: ${styles.colors.primary600}; line-height: 1.4;
`;

const ExecMethodSub = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400}; line-height: 1.5;
`;

const ExecMethodBadge = styled.span`
  display: inline-flex; align-items: center; margin-top: 8px; padding: 4px 8px;
  border-radius: 999px; background: rgba(89,100,255,0.10);
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  color: ${styles.colors.primary600}; line-height: 1.2;
`;

// ── Collapsible settings section (sending schedule, LinkedIn accounts) ────────

/* No `overflow: hidden` here — it clipped the account picker's dropdown panel. The
   header rounds its own corners instead so the hover fill still follows the border. */
const SettingsAccordion = styled.div<{ $invalid?: boolean }>`
  border: 1px solid ${({ $invalid }) => ($invalid ? styles.colors.red600 : '#dde3ee')};
  border-radius: 12px;
  background: ${styles.colors.white};
`;

const AccordionHeader = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 14px 18px;
  background: none; border: none; cursor: pointer; text-align: left;
  border-radius: 11px 11px 0 0;
  &:hover { background: #fafbfd; }
`;

const AccordionChevron = styled.span<{ $open: boolean }>`
  display: inline-flex; flex-shrink: 0; color: ${styles.colors.black400};
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const AccordionTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 800;
  color: ${styles.colors.black600};
`;

const AccordionSummary = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 600;
  color: ${styles.colors.black300};
  margin-left: 8px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const AccordionBody = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 4px 18px 20px;
  border-top: 1px solid #f1f4f9;
  padding-top: 16px;
`;

const ScheduleModeRow = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap;
  & > * { flex: 1 1 240px; }
`;

const ScheduleModeCard = styled.button<{ $active: boolean }>`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 14px 16px; border-radius: 10px; text-align: left; cursor: pointer;
  border: 1.5px solid ${({ $active }) => ($active ? styles.colors.primary600 : '#e4e8f0')};
  background: ${({ $active }) => ($active ? '#fbfcff' : styles.colors.white)};
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: ${styles.colors.primary400}; }
`;

const ScheduleRadio = styled.span<{ $active: boolean }>`
  width: 16px; min-width: 16px; height: 16px; margin-top: 2px;
  border-radius: 50%; box-sizing: border-box;
  border: ${({ $active }) => ($active ? `5px solid ${styles.colors.primary600}` : '1.5px solid #cbd5e1')};
  background: ${styles.colors.white};
  transition: border 0.15s;
`;

const ScheduleModeTitle = styled.span<{ $active: boolean }>`
  font-family: NunitoSans; font-size: 13.5px; font-weight: 700;
  color: ${({ $active }) => ($active ? styles.colors.black600 : styles.colors.black500)};
`;

const ScheduleModeSub = styled.span`
  font-family: NunitoSans; font-size: 12px; line-height: 1.45; color: ${styles.colors.black400};
`;

const AccountFieldLabel = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: ${styles.colors.black500};
`;

const ConnectedAccountRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  background: ${styles.colors.white}; border: 1px solid #e8edf5;
  border-radius: 10px; padding: 10px 12px;
`;

const AccountMetaRow = styled.div`
  display: flex; align-items: center; gap: 8px; min-width: 0;
`;

const OwnAccountTag = styled.span`
  font-weight: 600; color: ${styles.colors.black400};
`;

const AccountEmailText = styled.span`
  font-family: NunitoSans; font-size: 11.5px; color: ${styles.colors.black300};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const AccountCreditsText = styled.span`
  font-family: NunitoSans; font-size: 11.5px; font-weight: 600;
  color: ${styles.colors.primary600};
`;

const RemoveAccountBtn = styled.button`
  flex-shrink: 0; background: ${styles.colors.white}; border: 1px solid #dde3ee;
  border-radius: 6px; color: ${styles.colors.black500};
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  padding: 5px 12px; cursor: pointer;
  &:hover:not(:disabled) { border-color: #fca5a5; color: #dc2626; background: #fef2f2; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const CheckpointBox = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  border: 1px solid #fde68a; background: #fffbeb;
  border-radius: 10px; padding: 14px 16px;
`;

const ProxyConfigTitle = styled.div`
  font-family: NunitoSans; font-size: 11px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.07em; color: ${styles.colors.black400};
  display: flex; align-items: center; gap: 8px;
`;

const ProxyGrid = styled.div`display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end;`;

const ProxyLabel = styled.label`
  display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 160px;
`;

const ProxyLabelText = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: ${styles.colors.black400};
`;

const ProxyRequired = styled.span`color: #ef4444;`;

const ProxyHint = styled.p`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400}; line-height: 1.6; margin: 0;
  strong { font-weight: 700; color: ${styles.colors.black500}; }
`;

const CheckpointAltLink = styled.button`
  background: none; border: none; padding: 0; cursor: pointer; align-self: flex-start;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; color: ${styles.colors.primary600};
  &:hover { text-decoration: underline; }
  &:disabled { color: ${styles.colors.black400}; cursor: not-allowed; }
`;

const AccountsList = styled.div`display: flex; flex-direction: column; gap: 6px;`;

const AccountName = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const BADGE_TONES = {
  green: { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
  amber: { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  red:   { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
} as const;

const AccountStatusBadge = styled.span<{ $tone: keyof typeof BADGE_TONES }>`
  flex-shrink: 0; font-family: NunitoSans; font-size: 10.5px; font-weight: 700;
  padding: 2px 8px; border-radius: 99px;
  background: ${({ $tone }) => BADGE_TONES[$tone].bg};
  color: ${({ $tone }) => BADGE_TONES[$tone].color};
  border: 1px solid ${({ $tone }) => BADGE_TONES[$tone].border};
`;

const VerifyAccountBtn = styled.button`
  flex-shrink: 0; background: none; border: 1px solid #c7d2fe; border-radius: 6px;
  color: ${styles.colors.primary600}; font-family: NunitoSans; font-size: 11px; font-weight: 700;
  padding: 2px 8px; cursor: pointer;
  &:hover:not(:disabled) { background: #eef2ff; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const ConnectBtn = styled.button`
  height: 36px; padding: 0 16px; background: ${styles.colors.primary600}; color: ${styles.colors.white};
  border: none; border-radius: 8px; font-size: 12px; font-weight: 700; font-family: NunitoSans;
  cursor: pointer; white-space: nowrap;
  &:hover:not(:disabled) { background: ${styles.colors.primary400}; }
  &:disabled { background: ${styles.colors.black200}; cursor: default; }
`;

const SendingWindowGrid = styled.div`display: flex; gap: 16px; flex-wrap: wrap; align-items: flex-end;`;

const HourRangeRow = styled.div`display: flex; align-items: center; gap: 8px;`;

const HourSep = styled.span`
  font-family: NunitoSans; font-size: 13px; font-weight: 600; color: ${styles.colors.black300};
`;

const StyledSelect = styled.select`
  height: 36px; padding: 0 30px 0 12px; border: 1.5px solid #dde3ee; border-radius: 8px;
  font-size: 13px; font-family: NunitoSans; color: ${styles.colors.black600};
  background: ${styles.colors.white}; outline: none; width: 100%; box-sizing: border-box;
  appearance: none; cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23677084' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 11px center;
  &:focus { border-color: ${styles.colors.primary600}; box-shadow: 0 0 0 3px rgba(89,100,255,0.08); }
`;

const SendingSummary = styled.div<{ $warn?: boolean }>`
  display: inline-flex; align-items: center; gap: 8px; align-self: flex-start;
  padding: 7px 12px; border-radius: 8px;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; line-height: 1;
  background: ${({ $warn }) => $warn ? '#fff7ed' : '#eef2ff'};
  border: 1px solid ${({ $warn }) => $warn ? '#fed7aa' : '#dbe3ff'};
  color: ${({ $warn }) => $warn ? '#c2410c' : styles.colors.primary600};
  svg { flex-shrink: 0; }
`;

const SendingWindowDaysRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;

const DayToggle = styled.label<{ $active: boolean }>`
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  min-width: 44px; padding: 6px 14px; border-radius: 99px;
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  border: 1.5px solid ${({ $active }) => $active ? styles.colors.primary600 : '#dde3ee'};
  background: ${({ $active }) => $active ? styles.colors.primary600 : styles.colors.white};
  color: ${({ $active }) => $active ? styles.colors.white : styles.colors.black400};
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  &:hover { border-color: ${styles.colors.primary600}; }
  input[type="checkbox"] { display: none; }
`;

export default CampaignFormPage;
